'use client'

import { api, queryKeys } from '@/lib/api'
import {
  AlertCircle,
  ArrowLeft,
  CheckCircle,
  Languages,
  Mic,
  MicOff,
  Monitor,
  NotebookPen,
  PanelRightClose,
  PanelRightOpen,
  Paperclip,
  Phone,
  Send,
  Smartphone,
  BookOpen,
  Sparkles,
  User,
  Users,
  UserPlus,
  X,
} from 'lucide-react'
import type React from 'react'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import type { InfiniteData } from '@tanstack/react-query'
import NextImage from 'next/image'
import { useRouter } from '@/i18n/navigation'
import { useAgentAuth } from '@/stores/auth'
import {
  useUnifiedWebSocketConnection,
  useUnifiedWebSocketMessage,
} from '@/lib/websocket-unified'
import { useWebSocket } from '@/lib/websocket'
import ChatMarkdown from '@/components/chat/ChatMarkdown'
import { TypingIndicator } from '@/components/chat/LoadingSpinner'
import { MessageAttachments } from '@/components/chat/MessageAttachments'
import { TransferModal } from '@/components/agent/TransferModal'
import {
  SupervisorManageDialog,
  type SupervisorAvailableAgent,
  type SupervisorManageDialogSubmit,
} from '@/components/agent/SupervisorManageDialog'
import { useAgentChatStore } from '@/stores/agentChat'
import { useNotificationActions } from '@/lib/notifications'
import { ContactSection } from '@/components/agent/contact/ContactSection'
import {
  ContactFormValues,
  useContactForm,
} from '@/components/agent/contact/useContactForm'
import {
  formatDateTime,
  toDisplayString,
} from '@/components/agent/utils/display'
import { extractUserRoles } from '@/lib/roles'
import { UserRole } from '@/types/roles'
import {
  deriveSessionContactIdentifiers,
  getContactDisplayName,
  normalizeForComparison,
} from '@/lib/agent-contact-utils'
import { useAgentContactMatches } from '@/hooks/use-agent-contact-matches'
import type {
  AgentAssignedSessionsPage,
  Article,
  ArticlePayload,
  KnowledgeBaseAnswer,
} from '@/types'
import type { SessionTakeoverOptions } from '@/lib/api/services/sessions'
import { useTeamsStore } from '@/stores/teams'
import { useAgentTeams } from '@/hooks/useAgentTeams'
import { useAllTeams } from '@/hooks/useAllTeams'
import { useTeamNameById, createTeamNameLookup } from '@/hooks/useTeamNameById'
import {
  PERMISSIONS,
  buildPermissionSet,
  hasAnyPermission,
  hasPermission,
} from '@/lib/permissions'
import { cn } from '@/lib/utils'
import { derivePrimaryTeamId, normalizeTeamIds } from '@/lib/utils/teams'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Label } from '@/components/ui/label'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import { Badge } from '@/components/ui/badge'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import type { FlowAttachment } from '@/types/flow'

interface Message {
  id: string
  role: 'user' | 'assistant' | 'agent' | 'system'
  content: string
  timestamp: string
  imageUrl?: string
  imageS3Key?: string
  attachments?: FlowAttachment[]
  audioUrl?: string
  audioS3Key?: string
  fileUrl?: string
  fileS3Key?: string
  fileName?: string
  agentName?: string
  translation?: string | null // English translation of user message
  hasTranslation?: boolean // Whether translation is available
  needsTranslation?: boolean // Whether message is detected as non-English
  showTranslation?: boolean // Whether to show the translation in UI
  isTranslating?: boolean // Whether translation is currently being fetched
  dbMessageId?: string // Database message ID for translation API (may differ from client-generated id)
}
interface ChatSession {
  id: string
  userId?: string | number
  displayName?: string
  platform: string
  platformId?: string
  status: string
  isHandedOver: boolean
  createdAt: string
  lastMessageAt?: string
  messages: Message[]
  metadata?: Record<string, unknown>
  agentName?: string
  agentId?: number
  teamId?: number | null
  teamName?: string | null
  assignment?: {
    agentId?: number | null
    agentName?: string | null
    teamId?: number | null
    teamName?: string | null
    status?: string | null
    requestId?: number | null
    assignedAt?: string | null
    requestedAt?: string | null
    requestType?: string | null
    assignedBy?: number | null
  } | null
}

type TakeoverActionType =
  | 'takeover'
  | 'force_takeover'
  | 'assign_agent'
  | 'assign_team'

type TakeoverResult = {
  success: boolean
  message?: string
}

interface ArticleDraftState {
  title: string
  summary: string
  content: string
  tagsInput: string
  questions: string[]
}

interface AgentChatPanelProps {
  sessionId: string | null
}

const toNullableNumber = (value: unknown): number | null => {
  if (value == null) {
    return null
  }
  const numeric = Number.parseInt(String(value), 10)
  return Number.isFinite(numeric) ? numeric : null
}

/**
 * Detects if text contains non-English characters (CJK, Arabic, Hebrew, Cyrillic, Thai, etc.)
 * Used to determine if translation button should be shown for user messages.
 */
const detectNonEnglish = (text: string | undefined | null): boolean => {
  if (!text || typeof text !== 'string') return false
  // Match non-ASCII characters that indicate non-English content
  // Includes: CJK, Japanese Kana, Korean, Arabic, Hebrew, Cyrillic, Thai, Devanagari, etc.
  const nonEnglishPattern =
    /[\u0400-\u04FF\u0590-\u05FF\u0600-\u06FF\u0E00-\u0E7F\u0900-\u097F\u3040-\u309F\u30A0-\u30FF\u4E00-\u9FFF\uAC00-\uD7AF]/
  return nonEnglishPattern.test(text)
}

const normalizeSessionAssignment = (
  raw: unknown
): {
  agentId: number | null
  agentName: string | null
  teamId: number | null
  teamName: string | null
  status: string | null
  requestId: number | null
  assignedAt: string | null
  requestedAt: string | null
  requestType: string | null
  assignedBy: number | null
} | null => {
  if (!raw || typeof raw !== 'object') {
    return null
  }

  const assignment = raw as Record<string, unknown>
  const agentId = toNullableNumber(assignment.agentId)
  const teamId = toNullableNumber(assignment.teamId)
  const agentNameRaw =
    typeof assignment.agentName === 'string'
      ? assignment.agentName.trim()
      : null
  const teamNameRaw =
    typeof assignment.teamName === 'string' ? assignment.teamName.trim() : null
  const status =
    typeof assignment.status === 'string' && assignment.status.trim().length > 0
      ? assignment.status.trim()
      : null
  const requestId = toNullableNumber(assignment.requestId)
  const requestType =
    typeof assignment.requestType === 'string'
      ? assignment.requestType.trim()
      : null
  const assignedBy = toNullableNumber(assignment.assignedBy)

  const normalizeTimestamp = (value: unknown): string | null => {
    if (value instanceof Date) {
      return value.toISOString()
    }
    if (typeof value === 'string' && value.trim().length > 0) {
      return value
    }
    return null
  }

  const assignedAt = normalizeTimestamp(assignment.assignedAt)
  const requestedAt = normalizeTimestamp(assignment.requestedAt)

  return {
    agentId,
    agentName: agentNameRaw && agentNameRaw.length > 0 ? agentNameRaw : null,
    teamId,
    teamName: teamNameRaw && teamNameRaw.length > 0 ? teamNameRaw : null,
    status,
    requestId,
    assignedAt,
    requestedAt,
    requestType: requestType && requestType.length > 0 ? requestType : null,
    assignedBy,
  }
}

const normalizeSessionResponse = (
  raw: any,
  allTeams: any[] = []
): ChatSession => {
  const assignment = normalizeSessionAssignment(raw?.assignment)
  // Use ONLY session data from chat_sessions table, no fallback to assignment
  const agentId = toNullableNumber(raw?.agentId ?? null)
  const teamId = toNullableNumber(raw?.teamId ?? null)

  const agentNameRaw =
    typeof raw?.agentName === 'string' ? raw.agentName.trim() : ''
  const teamNameRaw =
    typeof raw?.teamName === 'string' ? raw.teamName.trim() : ''

  // Only use session's agentName, no fallback to assignment
  const agentName = agentNameRaw.length > 0 ? agentNameRaw : undefined

  // Use shared team name lookup logic
  const getTeamNameById = createTeamNameLookup(allTeams, {
    session: {
      teamId: raw?.teamId,
      teamName: raw?.teamName || undefined,
      assignment: assignment
        ? {
            teamId: assignment.teamId,
            teamName: assignment.teamName || undefined,
          }
        : undefined,
    },
  })

  // Only use session's teamName, no fallback to assignment
  const teamName =
    teamNameRaw.length > 0 ? teamNameRaw : getTeamNameById(teamId) || null

  const normalizedAssignment =
    assignment ||
    agentId != null ||
    agentName ||
    teamId != null ||
    (teamName != null && teamName.length > 0)
      ? {
          agentId,
          agentName: agentName ?? null,
          teamId,
          teamName:
            assignment?.teamName != null
              ? getTeamNameById(assignment.teamId) || assignment.teamName
              : teamName,
          status: assignment?.status ?? null,
          requestId: assignment?.requestId ?? null,
          assignedAt: assignment?.assignedAt ?? null,
          requestedAt: assignment?.requestedAt ?? null,
          requestType: assignment?.requestType ?? null,
          assignedBy: assignment?.assignedBy ?? null,
        }
      : null

  return {
    id: String(raw?.id ?? ''),
    userId: raw?.userId ?? undefined,
    displayName: raw?.displayName ?? undefined,
    platform: raw?.platform ?? 'web',
    platformId: raw?.platformId ?? undefined,
    status: raw?.status ?? 'active',
    isHandedOver:
      typeof raw?.isHandedOver === 'boolean'
        ? raw.isHandedOver
        : Boolean(raw?.is_handed_over),
    createdAt: raw?.createdAt ?? new Date().toISOString(),
    lastMessageAt: raw?.lastMessageAt ?? undefined,
    messages: Array.isArray(raw?.messages) ? raw.messages : [],
    metadata: toMetadataRecord(raw?.metadata),
    agentName: agentName ?? undefined,
    agentId: agentId ?? undefined,
    teamId,
    teamName,
    assignment: normalizedAssignment,
  }
}

const inferBrowserFromUserAgent = (userAgent: string): string | undefined => {
  const normalized = userAgent.toLowerCase()
  if (!normalized) return undefined
  if (normalized.includes('edg/')) return 'Microsoft Edge'
  if (normalized.includes('opr/') || normalized.includes('opera'))
    return 'Opera'
  if (
    normalized.includes('chrome') &&
    !normalized.includes('chromium') &&
    !normalized.includes('edg/') &&
    !normalized.includes('opr/') &&
    !normalized.includes('opera')
  ) {
    return 'Chrome'
  }
  if (
    normalized.includes('safari') &&
    !normalized.includes('chrome') &&
    !normalized.includes('crios')
  ) {
    return 'Safari'
  }
  if (normalized.includes('firefox')) return 'Firefox'
  if (normalized.includes('msie') || normalized.includes('trident'))
    return 'Internet Explorer'
  return undefined
}

const inferOsFromUserAgent = (userAgent: string): string | undefined => {
  const normalized = userAgent.toLowerCase()
  if (!normalized) return undefined
  if (normalized.includes('android')) return 'Android'
  if (
    normalized.includes('iphone') ||
    normalized.includes('ipad') ||
    normalized.includes('ipod') ||
    normalized.includes('ios')
  ) {
    return 'iOS'
  }
  if (normalized.includes('windows nt')) return 'Windows'
  if (
    normalized.includes('mac os x') ||
    normalized.includes('macintosh') ||
    normalized.includes('macintel')
  ) {
    return 'macOS'
  }
  if (normalized.includes('cros')) return 'ChromeOS'
  if (normalized.includes('linux')) return 'Linux'
  return undefined
}

function deriveMessagePreview(options: {
  content?: string | null
  imageUrl?: string | null
  audioUrl?: string | null
  fileUrl?: string | null
  fileName?: string | null
}): string {
  const { content, imageUrl, audioUrl, fileUrl, fileName } = options
  const trimmed = content?.trim()
  if (trimmed && trimmed.length > 0) {
    return trimmed
  }

  if (imageUrl) {
    return 'Sent an image'
  }

  if (audioUrl) {
    return 'Sent a voice message'
  }

  if (fileUrl || fileName) {
    const name = fileName?.trim()
    return name && name.length > 0 ? `Sent a file: ${name}` : 'Sent a file'
  }

  return 'New message received'
}

const toMetadataRecord = (
  value: unknown
): Record<string, unknown> | undefined => {
  if (value && typeof value === 'object' && !Array.isArray(value)) {
    return value as Record<string, unknown>
  }
  return undefined
}

export function AgentChatPanel({
  sessionId: sessionIdProp,
}: AgentChatPanelProps) {
  const router = useRouter()
  const activeSessionIdFromStore = useAgentChatStore(
    state => state.activeSessionId
  )
  const setActiveSessionId = useAgentChatStore(
    state => state.setActiveSessionId
  )
  const setActiveDashboardTab = useAgentChatStore(
    state => state.setActiveDashboardTab
  )
  const sessionId = sessionIdProp ?? activeSessionIdFromStore ?? null
  const isEmbedded = true
  const containerClass = isEmbedded
    ? 'flex flex-col h-full bg-white'
    : 'min-h-screen bg-gray-50 flex flex-col'
  const headerWrapperClass = isEmbedded
    ? 'px-4 py-4'
    : 'max-w-7xl mx-auto px-4 py-4'
  const mainContentWrapperClass = isEmbedded
    ? 'w-full px-4 py-6 flex flex-col gap-6 lg:flex-row'
    : 'max-w-7xl mx-auto w-full px-4 py-6 flex flex-col gap-6 lg:flex-row'

  // Counter for generating deterministic IDs
  const messageIdCounter = useRef(0)

  // Helper function to generate unique IDs (consistent for SSR)
  const generateUniqueId = (): string => {
    messageIdCounter.current += 1
    return `msg-${sessionId}-${messageIdCounter.current}`
  }
  const {
    user: agentUser,
    token,
    logout,
    verify,
    isAuthenticated,
    isLoading: authIsLoading,
  } = useAgentAuth()

  const permissionSet = useMemo(
    () => buildPermissionSet(agentUser?.permissions),
    [agentUser?.permissions]
  )

  const notificationActions = useNotificationActions()
  const queryClient = useQueryClient()
  const updateAssignedSessionSidebar = useCallback(
    (update: {
      sessionId: string
      preview: string
      timestamp?: string
      sender: 'agent' | 'guest' | 'bot' | 'system' | 'assistant' | null
      role: 'agent' | 'user' | 'system' | 'bot' | 'assistant'
      agentId?: number | null
    }) => {
      const queries = queryClient
        .getQueryCache()
        .findAll({ queryKey: ['agent-dashboard'], exact: false })

      for (const query of queries) {
        queryClient.setQueryData<InfiniteData<AgentAssignedSessionsPage>>(
          query.queryKey,
          oldData => {
            if (!oldData) {
              return oldData
            }

            if (!Array.isArray(oldData.pages) || oldData.pages.length === 0) {
              return oldData
            }

            let didUpdate = false
            const nextPages = oldData.pages.map(page => {
              let pageUpdated = false

              const nextSessions = page.sessions.map(session => {
                if (session.id !== update.sessionId) {
                  return session
                }

                pageUpdated = true
                didUpdate = true

                const nextTimestamp =
                  update.timestamp &&
                  !Number.isNaN(new Date(update.timestamp).getTime())
                    ? update.timestamp
                    : new Date().toISOString()

                const nextPreview =
                  update.preview && update.preview.trim().length > 0
                    ? update.preview
                    : session.lastMessagePreview

                const nextAgentId =
                  update.role === 'agent'
                    ? (update.agentId ?? session.lastMessageAgentId ?? null)
                    : (session.lastMessageAgentId ?? null)

                return {
                  ...session,
                  lastMessagePreview: nextPreview,
                  lastMessageAt: nextTimestamp,
                  lastMessageSender: update.sender,
                  lastMessageRole: update.role,
                  lastMessageAgentId: nextAgentId,
                  hasMessages: true,
                }
              })

              if (!pageUpdated) {
                return page
              }

              return {
                ...page,
                sessions: nextSessions,
              }
            })

            if (!didUpdate) {
              return oldData
            }

            return {
              ...oldData,
              pages: nextPages,
            }
          }
        )
      }
    },
    [queryClient]
  )
  const agentRoles = useMemo(() => extractUserRoles(agentUser), [agentUser])
  const isSupervisor = agentRoles.includes(UserRole.SUPERVISOR)

  const canTransfer = useMemo(
    () => hasPermission(permissionSet, 'CHAT_TRANSFER'),
    [permissionSet]
  )

  const canCreateArticles = useMemo(
    () => hasPermission(permissionSet, PERMISSIONS.ARTICLE_CREATE),
    [permissionSet]
  )

  const canManageArticles = useMemo(
    () =>
      hasAnyPermission(permissionSet, [
        PERMISSIONS.ARTICLE_CREATE,
        PERMISSIONS.ARTICLE_UPDATE_ALL,
        PERMISSIONS.ARTICLE_UPDATE_OWN,
        PERMISSIONS.ARTICLE_DELETE,
        PERMISSIONS.ANY,
      ]),
    [permissionSet]
  )

  const canUseKnowledgeBase = useMemo(
    () =>
      hasAnyPermission(permissionSet, [
        PERMISSIONS.ARTICLE_CREATE,
        PERMISSIONS.ARTICLE_UPDATE_ALL,
        PERMISSIONS.ARTICLE_UPDATE_OWN,
        PERMISSIONS.ARTICLE_DELETE,
        PERMISSIONS.ANY,
      ]),
    [permissionSet]
  )
  const resolvedAgentId = useMemo(() => {
    if (agentUser?.id == null) {
      return null
    }
    const numericId = Number(agentUser.id)
    return Number.isFinite(numericId) ? numericId : null
  }, [agentUser?.id])

  const [session, setSession] = useState<ChatSession | null>(null)
  const [messages, setMessages] = useState<Message[]>([])
  const [inputText, setInputText] = useState('')
  const [isLoading, setIsLoading] = useState(true)
  const [isSending, setIsSending] = useState(false)
  const [isRecording, setIsRecording] = useState(false)
  const [isAssignedToMe, setIsAssignedToMe] = useState(false)
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false)
  const [uploadedFile, setUploadedFile] = useState<{
    url: string
    file: File
    type: string
    isUploading?: boolean
    uploadError?: boolean
    s3Key?: string
    permanentUrl?: string
  } | null>(null)
  const [userTyping, setUserTyping] = useState(false)
  const [showTransferModal, setShowTransferModal] = useState(false)
  const [pendingTransfer, setPendingTransfer] = useState<{
    id: number
    status: string
    reason: string
    requestedAt?: string
    assignedToAgentName?: string | null
    assignedToAgentUsername?: string | null
    assignedToAgentId?: number | null
    teamId?: number | null
    teamName?: string | null
  } | null>(null)
  const [isAcceptingTransfer, setIsAcceptingTransfer] = useState(false)
  const [isAcceptingAssignment, setIsAcceptingAssignment] = useState(false)
  const [isSupervisorModalOpen, setIsSupervisorModalOpen] = useState(false)
  const [availableAgentsForSupervisor, setAvailableAgentsForSupervisor] =
    useState<SupervisorAvailableAgent[]>([])
  const [isFetchingSupervisorAgents, setIsFetchingSupervisorAgents] =
    useState(false)
  const [availableAgentsError, setAvailableAgentsError] = useState<
    string | null
  >(null)
  const [isSupervisorActionLoading, setIsSupervisorActionLoading] =
    useState(false)
  const [supervisorActionError, setSupervisorActionError] = useState<
    string | null
  >(null)
  const [isTakeoverActionPending, setIsTakeoverActionPending] = useState(false)
  const [showTakeoverTeamModal, setShowTakeoverTeamModal] = useState(false)
  const [selectedTakeoverTeamId, setSelectedTakeoverTeamId] = useState<
    number | null
  >(null)
  const [pendingTakeoverSessionId, setPendingTakeoverSessionId] = useState<
    string | null
  >(null)
  const [pendingTakeoverOptions, setPendingTakeoverOptions] = useState<
    SessionTakeoverOptions | undefined
  >(undefined)
  const shouldRestoreFocusRef = useRef(false)
  const messagesEndRef = useRef<HTMLDivElement>(null)
  const messagesContainerRef = useRef<HTMLDivElement>(null)
  const inputRef = useRef<HTMLTextAreaElement>(null)
  const mediaRecorderRef = useRef<MediaRecorder | null>(null)
  const audioChunksRef = useRef<Blob[]>([])
  const canReplyRef = useRef(true)
  const availableTeams = useTeamsStore(state => state.teamFilter.availableTeams)
  const fetchTeams = useTeamsStore(state => state.fetchTeams)
  const { teams: agentTeamsFromApi } = useAgentTeams()
  const { teams: allTeams } = useAllTeams()
  const [isKnowledgeModalOpen, setIsKnowledgeModalOpen] = useState(false)
  const [knowledgeQuery, setKnowledgeQuery] = useState('')
  const knowledgeMutation = useMutation<
    KnowledgeBaseAnswer,
    Error,
    { query: string }
  >({
    mutationFn: async ({ query }) => {
      const response = await api.agent.askKnowledge({
        query,
        sessionId: sessionId ?? undefined,
      })
      if (!response.success || !response.data) {
        throw new Error(
          response.error ||
            response.message ||
            'Unable to fetch knowledge answer'
        )
      }
      return response.data
    },
  })
  const knowledgeAnswerData = knowledgeMutation.data ?? null
  const knowledgeAnswer = knowledgeAnswerData?.answer ?? null
  const knowledgeSources = knowledgeAnswerData?.sources ?? []
  const isKnowledgeSearching = knowledgeMutation.isPending
  const knowledgeErrorMessage = knowledgeMutation.error?.message ?? null
  const [isArticleLibraryOpen, setIsArticleLibraryOpen] = useState(false)
  const [articleSearchTerm, setArticleSearchTerm] = useState('')
  const [articlePreviewId, setArticlePreviewId] = useState<number | null>(null)
  const [isSaveArticleOpen, setIsSaveArticleOpen] = useState(false)
  const [articleDraft, setArticleDraft] = useState<ArticleDraftState>({
    title: '',
    summary: '',
    content: '',
    tagsInput: '',
    questions: [],
  })
  const [articleDraftQuestionInput, setArticleDraftQuestionInput] = useState('')
  const normalizedArticleSearch = articleSearchTerm.trim()
  const articleSuggestionsQuery = useQuery({
    queryKey: queryKeys.agentArticles.suggestions(normalizedArticleSearch),
    queryFn: async () => {
      const response = await api.agent.searchArticles(normalizedArticleSearch, {
        limit: 10,
      })

      if (!response.success) {
        throw new Error(response.error || 'Unable to search articles')
      }

      return response
    },
    enabled: isArticleLibraryOpen && normalizedArticleSearch.length >= 2,
    staleTime: 30_000,
  })
  const articleSuggestions = articleSuggestionsQuery.data?.data ?? []

  const parseTags = useCallback((value: string) => {
    return value
      .split(',')
      .map(tag => tag.trim())
      .filter(tag => tag.length > 0)
  }, [])

  const openKnowledgeModal = useCallback(() => {
    if (!canUseKnowledgeBase) {
      notificationActions.showError(
        'Permission denied',
        'You do not have access to knowledge search.'
      )
      return
    }

    setKnowledgeQuery('')
    knowledgeMutation.reset()
    setIsKnowledgeModalOpen(true)
  }, [canUseKnowledgeBase, knowledgeMutation, notificationActions])

  const closeKnowledgeModal = useCallback(() => {
    setIsKnowledgeModalOpen(false)
    setKnowledgeQuery('')
    knowledgeMutation.reset()
    shouldRestoreFocusRef.current = true
  }, [knowledgeMutation])

  const handleKnowledgeSearch = useCallback(
    (event?: React.FormEvent<HTMLFormElement>) => {
      event?.preventDefault()
      if (!canUseKnowledgeBase) {
        notificationActions.showError(
          'Permission denied',
          'You do not have access to knowledge search.'
        )
        return
      }
      const trimmed = knowledgeQuery.trim()
      if (!trimmed) {
        notificationActions.showError(
          'Missing search query',
          'Enter a question or keyword to search the knowledge base.'
        )
        return
      }
      knowledgeMutation.mutate({ query: trimmed })
    },
    [
      canUseKnowledgeBase,
      knowledgeMutation,
      knowledgeQuery,
      notificationActions,
    ]
  )

  const handleInsertKnowledgeAnswer = useCallback(() => {
    if (!knowledgeAnswer) {
      notificationActions.showWarning(
        'No answer available',
        'The knowledge base did not return an answer to insert.'
      )
      return
    }
    setInputText(prev => {
      if (!prev.trim()) {
        return knowledgeAnswer
      }
      return `${prev.trim()}\n\n${knowledgeAnswer}`
    })
    shouldRestoreFocusRef.current = true
    closeKnowledgeModal()
  }, [closeKnowledgeModal, knowledgeAnswer, notificationActions])

  const resetArticleDraft = useCallback(() => {
    setArticleDraft({
      title: '',
      summary: '',
      content: '',
      tagsInput: '',
      questions: [],
    })
    setArticleDraftQuestionInput('')
  }, [])

  const closeSaveArticleDialog = useCallback(() => {
    setIsSaveArticleOpen(false)
    resetArticleDraft()
  }, [resetArticleDraft])

  const openSaveArticleDialog = useCallback(
    (message: Message) => {
      if (!canCreateArticles) {
        notificationActions.showError(
          'Permission denied',
          'You do not have access to create saved replies.'
        )
        return
      }

      const content = (message.content || '').trim()
      if (!content) {
        notificationActions.showError(
          'Cannot save message',
          'Only text replies can be saved as articles.'
        )
        return
      }

      const truncatedTitle =
        content.length > 64 ? `${content.slice(0, 64)}…` : content

      setArticleDraft({
        title: truncatedTitle,
        summary: '',
        content,
        tagsInput: '',
        questions: [],
      })
      setArticleDraftQuestionInput('')
      setIsSaveArticleOpen(true)
    },
    [canCreateArticles, notificationActions]
  )

  const addDraftQuestion = useCallback(() => {
    const value = articleDraftQuestionInput.trim()
    if (!value) {
      return
    }
    setArticleDraft(prev => ({
      ...prev,
      questions: [...prev.questions, value],
    }))
    setArticleDraftQuestionInput('')
  }, [articleDraftQuestionInput])

  const updateDraftQuestion = useCallback((index: number, value: string) => {
    setArticleDraft(prev => ({
      ...prev,
      questions: prev.questions.map((question, idx) =>
        idx === index ? value : question
      ),
    }))
  }, [])

  const removeDraftQuestion = useCallback((index: number) => {
    setArticleDraft(prev => ({
      ...prev,
      questions: prev.questions.filter((_, idx) => idx !== index),
    }))
  }, [])

  const saveArticleMutation = useMutation({
    mutationFn: async (payload: ArticlePayload) => {
      const response = await api.agent.createArticle(payload)
      if (!response.success || !response.data) {
        throw new Error(response.error || 'Unable to save article')
      }
      return response.data
    },
    onSuccess: () => {
      notificationActions.showSuccess(
        'Article saved',
        'This reply is now available in the article library.'
      )
      queryClient.invalidateQueries({ queryKey: queryKeys.agentArticles.all })
      closeSaveArticleDialog()
      setIsArticleLibraryOpen(false)
    },
    onError: (error: unknown) => {
      notificationActions.showError(
        'Save failed',
        error instanceof Error ? error.message : 'Unable to save article'
      )
    },
  })

  const handleSaveArticleDraft = useCallback(() => {
    if (!canCreateArticles) {
      notificationActions.showError(
        'Permission denied',
        'You do not have access to create saved replies.'
      )
      return
    }

    const title = articleDraft.title.trim()
    const content = articleDraft.content.trim()

    if (!title) {
      notificationActions.showError('Missing title', 'Please provide a title.')
      return
    }

    if (!content) {
      notificationActions.showError(
        'Missing content',
        'Content cannot be empty.'
      )
      return
    }

    const payload: ArticlePayload = {
      title,
      content,
      summary: articleDraft.summary.trim()
        ? articleDraft.summary.trim()
        : undefined,
      tags: parseTags(articleDraft.tagsInput),
      questions: articleDraft.questions
        .map(question => question.trim())
        .filter(question => question.length > 0),
      isActive: true,
    }

    saveArticleMutation.mutate(payload)
  }, [
    articleDraft,
    canCreateArticles,
    notificationActions,
    parseTags,
    saveArticleMutation,
  ])

  const openArticleLibrary = useCallback(() => {
    if (!canManageArticles) {
      notificationActions.showError(
        'Permission denied',
        'You do not have access to the saved replies library.'
      )
      return
    }

    setArticleSearchTerm('')
    setArticlePreviewId(null)
    setIsArticleLibraryOpen(true)
  }, [canManageArticles, notificationActions])

  const handleInsertArticleContent = useCallback((article: Article) => {
    setInputText(prev => {
      if (!prev.trim()) {
        return article.content
      }
      return `${prev.trim()}\n\n${article.content}`
    })
    shouldRestoreFocusRef.current = true
    setIsArticleLibraryOpen(false)
    setArticleSearchTerm('')
    setArticlePreviewId(null)
  }, [])

  const toggleSidebar = useCallback(() => {
    setIsSidebarCollapsed(prev => !prev)
  }, [])

  // Simple typing detection for agent
  const typingTimeoutRef = useRef<NodeJS.Timeout | null>(null)
  const [isAgentTyping, setIsAgentTyping] = useState(false)

  useEffect(() => {
    if (sessionId) {
      setActiveSessionId(sessionId)
      if (!isEmbedded) {
        setActiveDashboardTab('chat')
      }
    }
  }, [sessionId, setActiveDashboardTab, setActiveSessionId, isEmbedded])

  const handleExitToDashboard = useCallback(() => {
    if (isEmbedded) {
      setActiveDashboardTab('overview')
      return
    }

    router.push('/agent/dashboard')
  }, [isEmbedded, router, setActiveDashboardTab])

  const handleAgentTyping = (value: string) => {
    // Clear existing timeout
    if (typingTimeoutRef.current) {
      clearTimeout(typingTimeoutRef.current)
    }

    // If agent is typing and not already marked as typing
    if (value.trim() && !isAgentTyping) {
      setIsAgentTyping(true)
      if (sessionId && sendWsMessage) {
        sendWsMessage({
          type: 'agent_typing_start',
          data: { sessionId: sessionId as string },
        })
      }
    }

    // Set timeout to stop typing indicator
    if (value.trim()) {
      typingTimeoutRef.current = setTimeout(() => {
        setIsAgentTyping(false)
        if (sessionId && sendWsMessage) {
          sendWsMessage({
            type: 'agent_typing_stop',
            data: { sessionId: sessionId as string },
          })
        }
      }, 2000)
    } else {
      // If input is empty, stop typing immediately
      setIsAgentTyping(false)
      if (sessionId && sendWsMessage) {
        sendWsMessage({
          type: 'agent_typing_stop',
          data: { sessionId: sessionId as string },
        })
      }
    }
  }

  // Cleanup typing timeout on unmount
  useEffect(() => {
    return () => {
      if (typingTimeoutRef.current) {
        clearTimeout(typingTimeoutRef.current)
      }
    }
  }, [])

  // Verify authentication on mount
  useEffect(() => {
    let isCancelled = false

    const verifyAuth = async () => {
      // Wait a brief moment to allow for store rehydration from localStorage
      await new Promise(resolve => setTimeout(resolve, 0))

      if (isCancelled) return

      if (!isAuthenticated && !authIsLoading) {
        console.log('Not authenticated, redirecting to agent login')
        if (!isCancelled) {
          router.push('/agent')
        }
        return
      }

      if (isAuthenticated && !agentUser && !authIsLoading) {
        console.log('Authenticated but no user data, verifying token')
        const isValid = await verify()
        if (!isValid && !isCancelled) {
          console.log('Token verification failed, redirecting to login')
          router.push('/agent')
          return
        }
      }
    }

    verifyAuth()

    return () => {
      isCancelled = true
    }
  }, [isAuthenticated, agentUser, authIsLoading, verify, router])

  const handleTokenExpiration = useCallback(() => {
    logout()
    router.push('/agent')
  }, [logout, router])

  // Helper function to decode JWT and get agentId
  const getAgentIdFromToken = (token: string): number | null => {
    try {
      const payload = JSON.parse(atob(token.split('.')[1]))
      return payload.agentId || null
    } catch (error) {
      console.error('Failed to decode JWT token:', error)
      return null
    }
  }

  const checkSessionAssignment = useCallback(
    async (sessionDetails?: ChatSession | null) => {
      const agentId =
        agentUser?.id ?? (token ? getAgentIdFromToken(token) : null)

      if (!token || !agentId || !sessionId) {
        setIsAssignedToMe(false)
        return
      }

      try {
        let assignedAgentId =
          sessionDetails?.agentId ??
          (sessionDetails ? (sessionDetails as any)?.assignment?.agentId : null)

        if (assignedAgentId == null) {
          const sessionResponse = await api.sessions.getSession(
            sessionId as string
          )

          if (sessionResponse.success && sessionResponse.data) {
            assignedAgentId =
              sessionResponse.data.agentId ??
              (sessionResponse.data as any)?.assignment?.agentId ??
              null
          }
        }

        const numericAgentId = Number(agentId)
        const numericAssignedAgentId =
          assignedAgentId != null ? Number(assignedAgentId) : null

        if (
          numericAssignedAgentId != null &&
          !Number.isNaN(numericAssignedAgentId) &&
          numericAgentId === numericAssignedAgentId
        ) {
          setIsAssignedToMe(true)
          return
        }

        const response = await api.sessions.getActiveSessions()
        if (response.success && Array.isArray(response.data)) {
          // Check if session is ASSIGNED to current agent (not just PENDING for team)
          const isAssigned = response.data.some(
            (s: any) => s.id === sessionId && s.requestStatus === 'ASSIGNED'
          )
          setIsAssignedToMe(isAssigned)
        } else {
          setIsAssignedToMe(false)
        }
      } catch (error) {
        console.error('Error checking session assignment:', error)
        setIsAssignedToMe(false)
      }
    },
    [agentUser?.id, sessionId, token]
  )

  const loadSession = useCallback(async () => {
    try {
      if (!sessionId) {
        console.warn('No sessionId available, skipping session load')
        return
      }

      if (!token) {
        handleTokenExpiration()
        return
      }

      console.log('Loading session:', sessionId)

      const response = await api.sessions.getSession(sessionId as string)

      if (response.success && response.data) {
        const normalizedSession = normalizeSessionResponse(
          response.data,
          allTeams
        )
        setSession(normalizedSession)
        const messages = normalizedSession.messages || []

        const assignedAgentIdRaw =
          normalizedSession.agentId ??
          normalizedSession.assignment?.agentId ??
          null
        const currentAgentId =
          resolvedAgentId ?? (token ? getAgentIdFromToken(token) : null)

        if (
          assignedAgentIdRaw != null &&
          currentAgentId != null &&
          !Number.isNaN(Number(assignedAgentIdRaw)) &&
          !Number.isNaN(Number(currentAgentId)) &&
          Number(assignedAgentIdRaw) === Number(currentAgentId)
        ) {
          setIsAssignedToMe(true)
        } else if (assignedAgentIdRaw != null || response.data.isHandedOver) {
          setIsAssignedToMe(false)
        }

        // Filter out system messages for cleaner agent view
        const nonSystemMessages = messages.filter(
          (msg: any) => msg.role !== 'system'
        )

        // Fetch translations and detect which messages need translation
        try {
          const translationsResponse =
            await api.sessions.getSessionTranslations(sessionId as string, 'en')

          const responseData = translationsResponse.data ?? {}

          // Handle both old format (direct object) and new format ({ translations, needsTranslation })
          const translations = (
            typeof responseData === 'object' &&
            responseData !== null &&
            'translations' in responseData
              ? (responseData as any).translations
              : responseData
          ) as Record<string, string> | undefined

          const needsTranslation = (
            typeof responseData === 'object' &&
            responseData !== null &&
            'needsTranslation' in responseData
              ? (responseData as any).needsTranslation
              : {}
          ) as Record<string, boolean> | undefined

          // Apply both existing translations and needsTranslation flags
          const messagesWithTranslationInfo = nonSystemMessages.map(
            (msg: Message) => {
              if (msg.role === 'user') {
                const hasExistingTranslation =
                  translations && !!translations[msg.id]
                const needsTranslationFlag =
                  needsTranslation && needsTranslation[msg.id] === true

                const translationText =
                  (translations && translations[msg.id]) || undefined

                const inferredNeedsTranslation = Boolean(
                  translationText &&
                    msg.content &&
                    translationText.trim().length > 0 &&
                    translationText !== msg.content
                )

                return {
                  ...msg,
                  translation: translationText,
                  hasTranslation: hasExistingTranslation,
                  needsTranslation:
                    needsTranslationFlag || inferredNeedsTranslation,
                  showTranslation: false,
                }
              }
              return msg
            }
          )

          setMessages(messagesWithTranslationInfo)
        } catch (translationError) {
          console.error('Error fetching translations:', translationError)
          // Continue without translations if fetch fails
          setMessages(nonSystemMessages)
        }

        // console.log(
        //   'Session loaded successfully. Messages:',
        //   nonSystemMessages.length
        // )
        // Check if current agent is assigned to this session
        await checkSessionAssignment(response.data)
      } else {
        throw new Error(response.error || 'Failed to load session')
      }
    } catch (error) {
      console.error('Error loading session:', error)
      if (error && typeof error === 'object' && 'status' in error) {
        console.error('Response status:', (error as any).status)
      }
      // If session not found, redirect to dashboard
      handleExitToDashboard()
    } finally {
      setIsLoading(false)
    }
  }, [
    sessionId,
    token,
    resolvedAgentId,
    handleTokenExpiration,
    checkSessionAssignment,
    handleExitToDashboard,
    allTeams,
  ])
  const refreshSessionSnapshot = useCallback(async () => {
    if (!token) {
      return
    }

    try {
      const response = await api.sessions.getSession(sessionId as string)
      if (!response.success || !response.data) {
        return
      }

      const normalizedSession = normalizeSessionResponse(
        response.data,
        allTeams
      )

      setSession(prev => {
        if (!prev) {
          return normalizedSession
        }

        return {
          ...prev,
          ...normalizedSession,
          metadata:
            normalizedSession.metadata !== undefined
              ? normalizedSession.metadata
              : prev.metadata,
          agentName:
            normalizedSession.agentName !== undefined
              ? normalizedSession.agentName
              : prev.agentName,
          agentId:
            normalizedSession.agentId !== undefined
              ? normalizedSession.agentId
              : prev.agentId,
          teamId:
            normalizedSession.teamId !== undefined
              ? normalizedSession.teamId
              : (prev.teamId ?? null),
          teamName:
            normalizedSession.teamName !== undefined
              ? normalizedSession.teamName
              : (prev.teamName ?? null),
          assignment: normalizedSession.assignment ?? prev.assignment ?? null,
          messages: prev.messages,
        }
      })
    } catch (error) {
      console.error('Failed to refresh session snapshot:', error)
    }
  }, [sessionId, token, allTeams])

  // WebSocket connection for real-time updates using centralized store
  const agentId = token ? getAgentIdFromToken(token) : null
  const { sendMessage: sendWsMessage, registerAgent } = useWebSocket('', {
    onConnect: () => {
      console.log('✅ Agent chat WebSocket connected')
    },
  })

  const { isConnected: wsConnected, isRegistered } =
    useUnifiedWebSocketConnection(
      true,
      'agent',
      agentId && sessionId
        ? { agentId: Number(agentId), sessionId: sessionId as string }
        : undefined
    )

  // Re-register agent with sessionId when viewing a specific chat
  // This ensures direct message delivery via sessionConnections map
  useEffect(() => {
    if (!wsConnected || !agentId || !sessionId || !registerAgent) {
      return
    }

    console.log('[AgentChatPanel] 🔄 Re-registering agent with session:', {
      agentId: Number(agentId),
      sessionId: sessionId as string,
    })

    // Register agent with BOTH agentId AND sessionId for direct delivery
    registerAgent(Number(agentId), sessionId as string)
  }, [wsConnected, agentId, sessionId, registerAgent])

  // Subscribe to WebSocket messages with deduplication
  const processedMessageIds = useRef(new Set<string>())

  // Clean up old message IDs periodically to prevent memory leaks
  useEffect(() => {
    const cleanup = setInterval(() => {
      processedMessageIds.current.clear()
    }, 60000) // Clear every minute

    return () => clearInterval(cleanup)
  }, [])

  useUnifiedWebSocketMessage('connection_established', data => {
    console.log('✅ Agent WebSocket connection established:', data)
  })

  useUnifiedWebSocketMessage('registration_confirmed', data => {
    console.log('✅ Agent WebSocket registration confirmed:', data)
  })

  // Handle message translation available events
  useUnifiedWebSocketMessage('message_translation', data => {
    if (data.sessionId !== sessionId) {
      return
    }

    setMessages(prev => {
      // First try to find by ID
      const indexById = prev.findIndex(msg => msg.id === data.messageId)

      let targetIndex = indexById

      // If not found by ID, look for matching content in user messages (heuristic for WS/API race condition)
      if (targetIndex === -1 && data.content) {
        // Find latest user message with same content that doesn't have ID set or is recent
        // Check from end of array (latest messages)
        for (let i = prev.length - 1; i >= 0; i--) {
          const msg = prev[i]
          if (msg.role === 'user' && msg.content === data.content) {
            targetIndex = i
            break
          }
        }
      }

      if (targetIndex === -1) {
        return prev
      }

      const newMessages = [...prev]
      const msg = newMessages[targetIndex]

      const translationText =
        typeof data.translation === 'string' && data.translation.trim()
          ? data.translation
          : null

      const resolvedHasTranslation = translationText
        ? true
        : typeof data.hasTranslation === 'boolean'
          ? data.hasTranslation
          : (msg.hasTranslation ?? false)

      const fallbackNeedsTranslation =
        translationText && msg.content
          ? translationText.trim().length > 0 && translationText !== msg.content
          : (msg.needsTranslation ?? false)

      const resolvedNeedsTranslation =
        typeof data.needsTranslation === 'boolean'
          ? data.needsTranslation
          : fallbackNeedsTranslation

      newMessages[targetIndex] = {
        ...msg,
        // Update to the real DB ID if we matched by content/heuristic
        id: data.messageId || msg.id,
        dbMessageId: data.messageId || msg.dbMessageId || msg.id,
        needsTranslation: resolvedNeedsTranslation,
        hasTranslation: resolvedHasTranslation,
        translation: translationText ?? msg.translation,
        isTranslating: translationText ? false : msg.isTranslating,
      }
      return newMessages
    })
  })

  useUnifiedWebSocketMessage('user_message', data => {
    console.log('📥 [AGENT] Received user_message:', data)

    if (data.sessionId) {
      const preview = deriveMessagePreview({
        content: data.content,
        imageUrl: data.imageUrl,
        audioUrl: data.audioUrl,
        fileUrl: data.fileUrl,
        fileName: data.fileName,
      })

      updateAssignedSessionSidebar({
        sessionId: data.sessionId,
        preview,
        timestamp: data.timestamp,
        sender: 'guest',
        role: 'user',
      })
    }

    if (data.sessionId && data.sessionId === sessionId) {
      // Create a unique message identifier
      const messageKey = `user_message_${data.sessionId}_${data.messageId}_${data.timestamp}`

      if (processedMessageIds.current.has(messageKey)) {
        console.log('🔍 [DEBUG] Skipping duplicate user_message:', messageKey)
        return
      }

      processedMessageIds.current.add(messageKey)

      const userMessage: Message = {
        id: data.messageId || generateUniqueId(),
        role: 'user',
        content: data.content,
        timestamp: data.timestamp || new Date().toISOString(),
        imageUrl: data.imageUrl,
        imageS3Key: data.imageS3Key,
        audioUrl: data.audioUrl,
        audioS3Key: data.audioS3Key,
        fileUrl: data.fileUrl,
        fileS3Key: data.fileS3Key,
        fileName: data.fileName,
        showTranslation: false,
        needsTranslation: detectNonEnglish(data.content),
      }
      console.log('🔍 [DEBUG] User message WebSocket data:', {
        messageId: data.messageId,
        timestamp: data.timestamp,
        content: data.content?.substring(0, 50),
      })
      setMessages(prev => {
        // Check if message with same ID already exists
        const existingMessage = prev.find(msg => msg.id === userMessage.id)
        if (existingMessage) {
          console.log(
            '🔍 [DEBUG] User message with same ID already exists, skipping:',
            userMessage.id,
            'Existing:',
            existingMessage
          )
          return prev
        }
        console.log('📥 [DEBUG] Adding user message to chat:', userMessage)
        console.log('📊 [DEBUG] Current messages count:', prev.length)
        return [...prev, userMessage]
      })
      setSession(prev =>
        prev
          ? {
              ...prev,
              lastMessageAt: userMessage.timestamp ?? prev.lastMessageAt,
            }
          : prev
      )
      // Don't refresh session snapshot here - we already have the message via WebSocket
      // refreshSessionSnapshot()
    } else {
      console.log(
        '📥 [AGENT] Ignoring user_message for different session:',
        data.sessionId
      )
    }
  })

  useUnifiedWebSocketMessage('user_typing_start', data => {
    if (data.sessionId === sessionId) {
      const typingKey = `user_typing_start_${data.sessionId}_${data.timestamp}`
      if (processedMessageIds.current.has(typingKey)) {
        console.log('🔍 [DEBUG] Skipping duplicate user_typing_start')
        return
      }
      processedMessageIds.current.add(typingKey)
      setUserTyping(true)
    }
  })

  useUnifiedWebSocketMessage('user_typing_stop', data => {
    if (data.sessionId === sessionId) {
      const typingKey = `user_typing_stop_${data.sessionId}_${data.timestamp}`
      if (processedMessageIds.current.has(typingKey)) {
        console.log('🔍 [DEBUG] Skipping duplicate user_typing_stop')
        return
      }
      processedMessageIds.current.add(typingKey)
      setUserTyping(false)
    }
  })

  // Handle message translation available events
  useUnifiedWebSocketMessage('message_translation', data => {
    if (data.sessionId !== sessionId) {
      return
    }

    setMessages(prev => {
      // First try to find by ID
      const indexById = prev.findIndex(msg => msg.id === data.messageId)

      let targetIndex = indexById

      // If not found by ID, look for matching content in user messages (heuristic for WS/API race condition)
      if (targetIndex === -1 && data.content) {
        // Check from end of array (latest messages)
        for (let i = prev.length - 1; i >= 0; i--) {
          const msg = prev[i]
          if (msg.role === 'user' && msg.content === data.content) {
            targetIndex = i
            break
          }
        }
      }

      if (targetIndex === -1) {
        return prev
      }

      const newMessages = [...prev]
      const msg = newMessages[targetIndex]

      const translationText =
        typeof data.translation === 'string' && data.translation.trim()
          ? data.translation
          : null

      const resolvedHasTranslation = translationText
        ? true
        : typeof data.hasTranslation === 'boolean'
          ? data.hasTranslation
          : (msg.hasTranslation ?? false)

      const fallbackNeedsTranslation =
        translationText && msg.content
          ? translationText.trim().length > 0 && translationText !== msg.content
          : (msg.needsTranslation ?? false)

      const resolvedNeedsTranslation =
        typeof data.needsTranslation === 'boolean'
          ? data.needsTranslation
          : fallbackNeedsTranslation

      newMessages[targetIndex] = {
        ...msg,
        // Update to the real DB ID if we matched by content/heuristic
        id: data.messageId || msg.id,
        dbMessageId: data.messageId || msg.dbMessageId || msg.id,
        needsTranslation: resolvedNeedsTranslation,
        hasTranslation: resolvedHasTranslation,
        translation: translationText ?? msg.translation,
        isTranslating: translationText ? false : msg.isTranslating,
      }
      return newMessages
    })
  })

  // Handle agent messages (but skip our own)
  useUnifiedWebSocketMessage('agent_message', data => {
    console.log('📥 [AGENT] Received agent_message:', data)

    if (data.sessionId) {
      const preview = deriveMessagePreview({
        content: data.content,
        imageUrl: data.imageUrl,
        audioUrl: data.audioUrl,
        fileUrl: data.fileUrl,
        fileName: data.fileName,
      })

      updateAssignedSessionSidebar({
        sessionId: data.sessionId,
        preview,
        timestamp: data.timestamp,
        sender: 'agent',
        role: 'agent',
        agentId:
          typeof data.agentId === 'number'
            ? data.agentId
            : data.agentId != null && !Number.isNaN(Number(data.agentId))
              ? Number(data.agentId)
              : null,
      })
    }

    if (data.sessionId && data.sessionId === sessionId) {
      // Create a unique message identifier for deduplication
      const messageKey = `agent_message_${data.sessionId}_${data.messageId}_${data.timestamp}`

      if (processedMessageIds.current.has(messageKey)) {
        console.log('🔍 [DEBUG] Skipping duplicate agent_message:', messageKey)
        return
      }
      processedMessageIds.current.add(messageKey)

      const agentMessage: Message = {
        id: data.messageId || generateUniqueId(),
        role: 'agent',
        content: data.content,
        timestamp: data.timestamp || new Date().toISOString(),
        agentName: data.agentName,
        imageUrl: data.imageUrl,
        imageS3Key: data.imageS3Key,
        audioUrl: data.audioUrl,
        audioS3Key: data.audioS3Key,
        fileUrl: data.fileUrl,
        fileS3Key: data.fileS3Key,
        fileName: data.fileName,
      }

      setMessages(prev => {
        // Check if message with same ID already exists
        if (prev.some(msg => msg.id === agentMessage.id)) {
          console.log(
            '🔍 [DEBUG] Agent message with same ID already exists, skipping:',
            agentMessage.id
          )
          return prev
        }
        console.log('📥 [DEBUG] Adding agent message to chat:', agentMessage)
        return [...prev, agentMessage]
      })
      setSession(prev =>
        prev
          ? {
              ...prev,
              lastMessageAt: agentMessage.timestamp ?? prev.lastMessageAt,
            }
          : prev
      )
      // Don't refresh session snapshot here - we already have the message via WebSocket
      // refreshSessionSnapshot()
    } else {
      console.log(
        '📥 [AGENT] Ignoring agent_message for different session:',
        data.sessionId
      )
    }
  })

  // Handle assistant (AI) messages
  useUnifiedWebSocketMessage('assistant_message', data => {
    console.log('📥 [AGENT] Received assistant_message:', data)

    if (data.sessionId && data.sessionId === sessionId) {
      // Create a unique message identifier for deduplication
      const messageKey = `assistant_message_${data.sessionId}_${data.messageId}_${data.timestamp}`

      if (processedMessageIds.current.has(messageKey)) {
        console.log(
          '🔍 [DEBUG] Skipping duplicate assistant_message:',
          messageKey
        )
        return
      }

      processedMessageIds.current.add(messageKey)

      const preview = deriveMessagePreview({
        content: data.content,
        imageUrl: data.imageUrl,
        audioUrl: data.audioUrl,
        fileUrl: data.fileUrl,
        fileName: data.fileName,
      })

      updateAssignedSessionSidebar({
        sessionId: data.sessionId,
        preview,
        timestamp: data.timestamp,
        sender: 'assistant',
        role: 'assistant',
      })

      const assistantMessage: Message = {
        id: data.messageId || generateUniqueId(),
        role: 'assistant',
        content: data.content || '',
        timestamp: data.timestamp
          ? new Date(data.timestamp).toISOString()
          : new Date().toISOString(),
        imageUrl: data.imageUrl,
        audioUrl: data.audioUrl,
        fileUrl: data.fileUrl,
        fileName: data.fileName,
      }

      console.log('🔍 [DEBUG] Assistant message WebSocket data:', {
        messageId: data.messageId,
        timestamp: data.timestamp,
        content: data.content?.substring(0, 50),
      })
      setMessages(prev => {
        // Check if message with same ID already exists
        const existingMessage = prev.find(msg => msg.id === assistantMessage.id)
        if (existingMessage) {
          console.log(
            '🔍 [DEBUG] Assistant message with same ID already exists, skipping:',
            assistantMessage.id,
            'Existing:',
            existingMessage
          )
          return prev
        }
        console.log(
          '📥 [DEBUG] Adding assistant message to chat:',
          assistantMessage
        )
        console.log('📊 [DEBUG] Current messages count:', prev.length)
        return [...prev, assistantMessage]
      })
      setSession(prev =>
        prev
          ? {
              ...prev,
              lastMessageAt: assistantMessage.timestamp ?? prev.lastMessageAt,
            }
          : prev
      )
      // Don't refresh session snapshot here - we already have the message via WebSocket
      // refreshSessionSnapshot()
    } else {
      console.log(
        '📥 [AGENT] Ignoring assistant_message for different session:',
        data.sessionId
      )
    }
  })

  // Handle typing indicators (but skip our own)
  useUnifiedWebSocketMessage('agent_typing_start', data => {
    if (data.sessionId && data.sessionId === sessionId) {
      const currentAgentId = agentId
      if (
        data.agentId &&
        currentAgentId &&
        String(data.agentId) === String(currentAgentId)
      ) {
        console.log('🔍 [DEBUG] Skipping own agent_typing_start')
        return
      }
      // Handle other agent typing if needed
    }
  })

  useUnifiedWebSocketMessage('agent_typing_stop', data => {
    if (data.sessionId === sessionId) {
      const currentAgentId = agentId
      if (
        data.agentId &&
        currentAgentId &&
        String(data.agentId) === String(currentAgentId)
      ) {
        console.log('🔍 [DEBUG] Skipping own agent_typing_stop')
        return
      }
      // Handle other agent typing if needed
    }
  })

  useUnifiedWebSocketMessage('session_assigned', data => {
    // Session was assigned to an agent, refresh assignment check
    if (data.sessionId && data.sessionId === sessionId) {
      const currentAgentId =
        agentUser?.id || (token ? getAgentIdFromToken(token) : null)

      // Immediate update if this agent was assigned
      if (data.agentId === currentAgentId) {
        setIsAssignedToMe(true)
      } else {
        setIsAssignedToMe(false)
      }

      refreshSessionSnapshot()

      // Also refresh assignment check as backup after a brief delay
      setTimeout(() => {
        checkSessionAssignment()
        checkPendingTransfer()
      }, 1000)
    }
  })

  useUnifiedWebSocketMessage('session_updated', data => {
    if (data.sessionId && data.sessionId === sessionId) {
      if (typeof data.agentId === 'number') {
        const currentAgentId =
          agentUser?.id || (token ? getAgentIdFromToken(token) : null)
        if (
          currentAgentId != null &&
          Number(data.agentId) === Number(currentAgentId)
        ) {
          setIsAssignedToMe(true)
        } else {
          setIsAssignedToMe(false)
        }
      } else {
        setIsAssignedToMe(false)
      }
      refreshSessionSnapshot()
    }
  })

  useUnifiedWebSocketMessage('session_transferred', data => {
    if (data.sessionId && data.sessionId === sessionId) {
      refreshSessionSnapshot()
      checkSessionAssignment()
      checkPendingTransfer()
    }
  })

  useUnifiedWebSocketMessage('session_taken', data => {
    // Another agent accepted the handover assignment
    if (data.sessionId && data.sessionId === sessionId) {
      const currentAgentId =
        agentUser?.id || (token ? getAgentIdFromToken(token) : null)

      // Update assignment status immediately
      if (data.agentId === currentAgentId) {
        setIsAssignedToMe(true)
      } else {
        setIsAssignedToMe(false)
      }

      // Refresh session data to update assignment details and hide accept banner
      refreshSessionSnapshot()
      checkSessionAssignment()
      checkPendingTransfer()
    }
  })

  const checkPendingTransfer = useCallback(async () => {
    try {
      if (!sessionId) {
        setPendingTransfer(null)
        return
      }

      const response = await api.sessions.getTransferStatus(sessionId as string)
      if (response.success && response.data?.transferRequest) {
        const transfer = response.data.transferRequest
        const assignedToValue = transfer.assignedTo
        const assignedToAgentId =
          typeof assignedToValue === 'number'
            ? assignedToValue
            : typeof assignedToValue === 'string' &&
                assignedToValue.trim().length > 0 &&
                !Number.isNaN(Number(assignedToValue))
              ? Number(assignedToValue)
              : null
        const derivedId =
          typeof transfer.id === 'number'
            ? transfer.id
            : Number.parseInt(String(transfer.id), 10)

        if (!Number.isFinite(derivedId)) {
          console.warn(
            'Received invalid handover request id from transfer status response:',
            transfer.id
          )
          setPendingTransfer(null)
          return
        }

        const teamIdValue =
          typeof transfer.teamId === 'number'
            ? transfer.teamId
            : typeof transfer.teamId === 'string' &&
                transfer.teamId.trim().length > 0 &&
                !Number.isNaN(Number(transfer.teamId))
              ? Number(transfer.teamId)
              : null
        setPendingTransfer({
          id: derivedId,
          status:
            typeof transfer.status === 'string' && transfer.status.length > 0
              ? transfer.status
              : 'pending',
          reason: typeof transfer.reason === 'string' ? transfer.reason : '',
          requestedAt:
            transfer.requestedAt instanceof Date
              ? transfer.requestedAt.toISOString()
              : typeof transfer.requestedAt === 'string'
                ? transfer.requestedAt
                : undefined,
          assignedToAgentName:
            typeof transfer.assignedToName === 'string'
              ? transfer.assignedToName
              : undefined,
          assignedToAgentUsername:
            typeof transfer.assignedToUsername === 'string'
              ? transfer.assignedToUsername
              : undefined,
          assignedToAgentId,
          teamId: teamIdValue,
          teamName:
            typeof transfer.teamName === 'string'
              ? transfer.teamName
              : undefined,
        })
      } else {
        setPendingTransfer(null)
      }
    } catch (error) {
      console.error('Error checking pending transfer:', error)
      setPendingTransfer(null)
    }
  }, [sessionId])

  // Load session when authenticated and agentUser is available
  useEffect(() => {
    if (isAuthenticated && agentUser && token && !authIsLoading) {
      loadSession()
      checkPendingTransfer()
    }
  }, [
    isAuthenticated,
    agentUser,
    token,
    authIsLoading,
    loadSession,
    checkPendingTransfer,
  ])

  // Also check assignment whenever agentUser changes
  useEffect(() => {
    if (isAuthenticated && agentUser && token && sessionId) {
      checkSessionAssignment()
    }
  }, [
    agentUser?.id,
    checkSessionAssignment,
    isAuthenticated,
    token,
    sessionId,
    agentUser,
  ])

  useEffect(() => {
    if (!isSupervisor || !isSupervisorModalOpen) {
      if (!isSupervisorModalOpen) {
        setAvailableAgentsForSupervisor([])
        setAvailableAgentsError(null)
      }
      return
    }

    let cancelled = false
    const fetchAgents = async () => {
      setIsFetchingSupervisorAgents(true)
      setAvailableAgentsError(null)
      try {
        const response = await api.agent.listAgents()
        if (cancelled) {
          return
        }
        if (response.success) {
          const agentsArray = Array.isArray(response.data)
            ? (response.data as any[])
            : []

          const processedAgents = agentsArray.map(rawAgent => {
            const fallbackCandidate =
              (rawAgent as any)?.primaryTeamId ??
              (rawAgent as any)?.teamId ??
              null
            const normalizedTeamIds = normalizeTeamIds(
              (rawAgent as any)?.teamIds,
              fallbackCandidate
            )
            const primaryTeamId = derivePrimaryTeamId(
              normalizedTeamIds,
              fallbackCandidate
            )

            return {
              ...rawAgent,
              teamIds: normalizedTeamIds,
              primaryTeamId,
              teamId: primaryTeamId ?? (rawAgent as any)?.teamId ?? null,
              isOnline:
                typeof (rawAgent as any)?.isOnline === 'boolean'
                  ? Boolean((rawAgent as any)?.isOnline)
                  : null,
            } as SupervisorAvailableAgent
          })

          const seenIds = new Set<number>()
          const seenUsernames = new Set<string>()

          const dedupedAgents = processedAgents.filter(agent => {
            const normalizedUsername =
              typeof agent?.username === 'string'
                ? agent.username.trim().toLowerCase()
                : null

            if (agent?.id != null) {
              const numericId = Number(agent.id)
              if (Number.isFinite(numericId)) {
                if (seenIds.has(numericId)) {
                  return false
                }
                seenIds.add(numericId)
                if (normalizedUsername) {
                  seenUsernames.add(normalizedUsername)
                }
                return true
              }
            }

            if (normalizedUsername) {
              if (seenUsernames.has(normalizedUsername)) {
                return false
              }
              seenUsernames.add(normalizedUsername)
              return true
            }

            return true
          })

          dedupedAgents.sort((a, b) => {
            const onlineA = Number(Boolean(a.isOnline))
            const onlineB = Number(Boolean(b.isOnline))
            if (onlineA !== onlineB) {
              return onlineB - onlineA
            }

            const nameA = (
              a.firstName && a.lastName
                ? `${a.firstName} ${a.lastName}`
                : a.username || ''
            ).toLowerCase()
            const nameB = (
              b.firstName && b.lastName
                ? `${b.firstName} ${b.lastName}`
                : b.username || ''
            ).toLowerCase()
            return nameA.localeCompare(nameB)
          })

          setAvailableAgentsForSupervisor(dedupedAgents)
        } else {
          setAvailableAgentsForSupervisor([])
          setAvailableAgentsError(
            response.error || 'Failed to load available agents'
          )
        }
      } catch (error) {
        if (cancelled) {
          return
        }
        console.error('Failed to load available agents:', error)
        setAvailableAgentsForSupervisor([])
        const errorMessage =
          error instanceof Error && error.message
            ? error.message
            : 'Failed to load available agents'
        setAvailableAgentsError(errorMessage)
      } finally {
        if (!cancelled) {
          setIsFetchingSupervisorAgents(false)
        }
      }
    }

    fetchAgents()

    return () => {
      cancelled = true
    }
  }, [isSupervisor, isSupervisorModalOpen])

  useEffect(() => {
    if (!isSupervisorModalOpen) {
      return
    }

    if (availableTeams.length === 0) {
      fetchTeams().catch(error => {
        console.error('Failed to load teams for supervisor modal:', error)
      })
    }
  }, [availableTeams.length, fetchTeams, isSupervisorModalOpen])

  const agentTeams = useMemo(() => {
    if (agentTeamsFromApi.length > 0) {
      return agentTeamsFromApi
    }
    if (!Array.isArray(availableTeams) || availableTeams.length === 0) {
      return []
    }

    const rawTeamIds =
      agentUser && Array.isArray((agentUser as any)?.teamIds)
        ? ((agentUser as any)?.teamIds as Array<number | string | null>)
        : []

    const normalizedIds = rawTeamIds
      .map(value => {
        const numeric = Number.parseInt(String(value), 10)
        return Number.isFinite(numeric) ? numeric : null
      })
      .filter((value): value is number => value != null)

    if (normalizedIds.length === 0) {
      return availableTeams
    }

    const teamIdSet = new Set(normalizedIds)
    return availableTeams.filter(team => teamIdSet.has(team.id))
  }, [agentTeamsFromApi, agentUser, availableTeams])

  const agentTeamIds = useMemo(
    () => agentTeams.map(team => team.id),
    [agentTeams]
  )

  const supervisorDefaultTeamId = useMemo(() => {
    const raw =
      session?.teamId ??
      (session?.assignment?.teamId != null ? session.assignment.teamId : null)
    if (raw == null) {
      return null
    }
    const parsed = Number.parseInt(String(raw), 10)
    return Number.isFinite(parsed) ? parsed : null
  }, [session?.assignment?.teamId, session?.teamId])

  const getTeamNameById = useTeamNameById({
    session: session
      ? {
          teamId: session.teamId,
          teamName: session.teamName || undefined,
          assignment: session.assignment
            ? {
                teamId: session.assignment.teamId,
                teamName: session.assignment.teamName || undefined,
              }
            : undefined,
        }
      : undefined,
    pendingTransfer: pendingTransfer
      ? {
          teamId: pendingTransfer.teamId || undefined,
          teamName: pendingTransfer.teamName || undefined,
        }
      : undefined,
  })

  type AssignmentCachePatch = {
    agentId: number | null
    agentName: string | null
    teamId: number | null
    teamName: string | null
  }

  const updateDashboardAssignmentCaches = useCallback(
    (targetSessionId: string, patch: AssignmentCachePatch) => {
      const normalizedAgentId = patch.agentId ?? null
      const normalizedAgentName = patch.agentName ?? null
      const normalizedTeamId = patch.teamId ?? null
      const normalizedTeamName = patch.teamName ?? null

      const patchSessionRecord = (session: any) => {
        if (!session || session.id !== targetSessionId) {
          return session
        }

        let didChange = false
        const nextSession: any = { ...session }

        if (nextSession.agentName !== normalizedAgentName) {
          nextSession.agentName = normalizedAgentName
          didChange = true
        }
        if (Object.prototype.hasOwnProperty.call(nextSession, 'agentId')) {
          if (nextSession.agentId !== normalizedAgentId) {
            nextSession.agentId = normalizedAgentId
            didChange = true
          }
        }
        if (Object.prototype.hasOwnProperty.call(nextSession, 'teamId')) {
          if (nextSession.teamId !== normalizedTeamId) {
            nextSession.teamId = normalizedTeamId
            didChange = true
          }
        }
        if (Object.prototype.hasOwnProperty.call(nextSession, 'teamName')) {
          if (nextSession.teamName !== normalizedTeamName) {
            nextSession.teamName = normalizedTeamName
            didChange = true
          }
        }
        if (
          Object.prototype.hasOwnProperty.call(nextSession, 'assignedAgentName')
        ) {
          if (nextSession.assignedAgentName !== normalizedAgentName) {
            nextSession.assignedAgentName = normalizedAgentName
            didChange = true
          }
        }
        if (
          Object.prototype.hasOwnProperty.call(nextSession, 'assignedAgentId')
        ) {
          if (nextSession.assignedAgentId !== normalizedAgentId) {
            nextSession.assignedAgentId = normalizedAgentId
            didChange = true
          }
        }
        if (
          Object.prototype.hasOwnProperty.call(
            nextSession,
            'isOwnedByCurrentAgent'
          )
        ) {
          if (nextSession.isOwnedByCurrentAgent) {
            nextSession.isOwnedByCurrentAgent = false
            didChange = true
          }
        }
        if (
          Object.prototype.hasOwnProperty.call(nextSession, 'assignment') &&
          nextSession.assignment &&
          typeof nextSession.assignment === 'object'
        ) {
          const nextAssignment = {
            ...nextSession.assignment,
            agentId: normalizedAgentId,
            agentName: normalizedAgentName,
            teamId: normalizedTeamId,
            teamName: normalizedTeamName,
          }
          if (
            nextAssignment.agentId !== nextSession.assignment.agentId ||
            nextAssignment.agentName !== nextSession.assignment.agentName ||
            nextAssignment.teamId !== nextSession.assignment.teamId ||
            nextAssignment.teamName !== nextSession.assignment.teamName
          ) {
            nextSession.assignment = nextAssignment
            didChange = true
          }
        } else if (
          normalizedTeamId !== null ||
          normalizedTeamName !== null ||
          normalizedAgentId !== null ||
          normalizedAgentName !== null
        ) {
          nextSession.assignment = {
            agentId: normalizedAgentId,
            agentName: normalizedAgentName,
            teamId: normalizedTeamId,
            teamName: normalizedTeamName,
          }
          didChange = true
        }

        if (!didChange) {
          return session
        }
        return nextSession
      }

      const applyPatchToList = (list: any[]) => {
        let listChanged = false
        const nextList = list.map(item => {
          const patched = patchSessionRecord(item)
          if (patched !== item) {
            listChanged = true
          }
          return patched
        })
        return listChanged ? nextList : list
      }

      const queries = queryClient
        .getQueryCache()
        .findAll({ queryKey: ['agent-dashboard'], exact: false })

      for (const query of queries) {
        queryClient.setQueryData(query.queryKey, (previous: any) => {
          if (previous == null) {
            return previous
          }

          if (Array.isArray(previous)) {
            return applyPatchToList(previous)
          }

          if (
            typeof previous === 'object' &&
            previous !== null &&
            'pages' in previous &&
            Array.isArray((previous as any).pages)
          ) {
            let didUpdate = false
            const nextPages = (previous as any).pages.map((page: any) => {
              if (Array.isArray(page)) {
                const updatedList = applyPatchToList(page)
                if (updatedList !== page) {
                  didUpdate = true
                  return updatedList
                }
                return page
              }

              if (
                page &&
                typeof page === 'object' &&
                Array.isArray(page.sessions)
              ) {
                const updatedSessions = applyPatchToList(page.sessions)
                if (updatedSessions !== page.sessions) {
                  didUpdate = true
                  return { ...page, sessions: updatedSessions }
                }
                return page
              }

              return page
            })

            if (!didUpdate) {
              return previous
            }
            return { ...(previous as any), pages: nextPages }
          }

          if (
            typeof previous === 'object' &&
            previous !== null &&
            Array.isArray((previous as any).sessions)
          ) {
            const updatedSessions = applyPatchToList((previous as any).sessions)
            if (updatedSessions === (previous as any).sessions) {
              return previous
            }
            return { ...(previous as any), sessions: updatedSessions }
          }

          return previous
        })
      }
    },
    [queryClient]
  )

  const applySupervisorAssignmentSnapshot = useCallback(
    (targetSessionId: string, teamId: number | null) => {
      const normalizedTeamId =
        teamId != null && Number.isFinite(teamId) ? teamId : null
      const teamName = getTeamNameById(normalizedTeamId)
      let nextTeamName = teamName ?? null

      setIsAssignedToMe(false)
      setSession(prev => {
        if (!prev || prev.id !== targetSessionId) {
          return prev
        }
        const resolvedTeamName = teamName ?? prev.assignment?.teamName ?? null
        nextTeamName = resolvedTeamName
        const nextAssignment = prev.assignment
          ? {
              ...prev.assignment,
              agentId: null,
              agentName: null,
              teamId: normalizedTeamId,
              teamName: resolvedTeamName,
            }
          : {
              agentId: null,
              agentName: null,
              teamId: normalizedTeamId,
              teamName: resolvedTeamName,
            }
        return {
          ...prev,
          agentId: null,
          agentName: undefined,
          teamId: normalizedTeamId,
          teamName: resolvedTeamName ?? null,
          assignment: nextAssignment,
        }
      })

      updateDashboardAssignmentCaches(targetSessionId, {
        agentId: null,
        agentName: null,
        teamId: normalizedTeamId,
        teamName: nextTeamName,
      })
    },
    [getTeamNameById, setSession, updateDashboardAssignmentCaches]
  )

  const releaseSessionToTeam = useCallback(
    async (
      targetSessionId: string,
      targetTeamId: number,
      reason?: string
    ): Promise<TakeoverResult> => {
      try {
        const response = await api.sessions.transferSessionToTeam(
          targetSessionId,
          targetTeamId,
          reason
        )

        if (!response.success) {
          const message =
            response.error || response.message || 'Failed to release session'
          notificationActions.showError('Team Assignment Failed', message)
          return { success: false, message }
        }

        const successMessage =
          response.message ||
          `Session ${targetSessionId.slice(-8).toUpperCase()} returned to the team queue.`

        notificationActions.showSuccess('Session Released', successMessage)
        applySupervisorAssignmentSnapshot(targetSessionId, targetTeamId)
        await checkSessionAssignment()
        await refreshSessionSnapshot()
        await checkPendingTransfer()

        return { success: true, message: successMessage }
      } catch (error) {
        console.error('Failed to release session to team:', error)
        const message =
          error instanceof Error
            ? error.message
            : 'Failed to release session to the selected team'
        notificationActions.showError('Team Assignment Failed', message)
        return { success: false, message }
      }
    },
    [
      applySupervisorAssignmentSnapshot,
      checkPendingTransfer,
      checkSessionAssignment,
      notificationActions,
      refreshSessionSnapshot,
    ]
  )

  const scrollMessagesToBottom = useCallback(() => {
    if (typeof window === 'undefined') {
      return
    }

    const container = messagesContainerRef.current
    if (!container) {
      return
    }

    requestAnimationFrame(() => {
      const activeContainer = messagesContainerRef.current
      if (!activeContainer) {
        return
      }
      activeContainer.scrollTop = activeContainer.scrollHeight
    })
  }, [])

  useEffect(() => {
    if (isLoading) {
      return
    }
    // Wait until messages are mounted before scrolling to the latest entry
    scrollMessagesToBottom()
  }, [messages, isLoading, scrollMessagesToBottom])

  // Handle focus restoration after state updates
  useEffect(() => {
    if (shouldRestoreFocusRef.current && inputRef.current && !isSending) {
      inputRef.current.focus()
      shouldRestoreFocusRef.current = false
    }
  }, [isSending, inputText])

  const sendMessage = async (
    messageType: 'text' | 'voice' | 'file' = 'text'
  ) => {
    if ((!inputText.trim() && !uploadedFile) || isSending || !agentUser) {
      return
    }

    if (!canReplyRef.current) {
      notificationActions.showWarning(
        'Accept handover request',
        'Accept the handover request before replying to the guest.'
      )
      return
    }

    // Don't send if file is still uploading
    if (uploadedFile?.isUploading) {
      console.log('File is still uploading, please wait...')
      return
    }

    setIsSending(true)
    const messageText = inputText.trim()

    // Mark that we should restore focus after the operation
    shouldRestoreFocusRef.current = true

    setInputText('')

    try {
      if (!token) {
        handleTokenExpiration()
        return
      }

      // Prepare default content for image/file uploads when no text provided
      let finalContent = messageText
      if (!finalContent && uploadedFile) {
        if (uploadedFile.type === 'image') {
          finalContent = 'sent an image'
        } else if (uploadedFile.type === 'file') {
          finalContent = 'sent a file'
        } else if (uploadedFile.type === 'voice') {
          finalContent = 'sent a voice message'
        }
      }

      const requestData: any = {
        content: finalContent || '',
        type: messageType,
      }

      if (uploadedFile) {
        if (uploadedFile.type === 'image') {
          // Send permanent public S3 URL for storage (never expires)
          // But use presigned URL for immediate display (works with Next.js Image)
          requestData.imageUrl = uploadedFile.permanentUrl
          requestData.imageDisplayUrl = uploadedFile.url // presigned URL for UI display
          requestData.fileName = uploadedFile.file.name
          if (uploadedFile.s3Key) {
            requestData.imageS3Key = uploadedFile.s3Key
          }
        } else if (uploadedFile.type === 'file') {
          // Send permanent public S3 URL for storage
          requestData.fileUrl = uploadedFile.permanentUrl
          requestData.fileDisplayUrl = uploadedFile.url // presigned URL for UI display
          requestData.fileName = uploadedFile.file.name
          if (uploadedFile.s3Key) {
            requestData.fileS3Key = uploadedFile.s3Key
            console.log(
              '✅ [Frontend] File S3 Key captured:',
              uploadedFile.s3Key
            )
          } else {
            console.warn(
              '⚠️ [Frontend] No S3 Key found for uploaded file:',
              uploadedFile
            )
          }
        } else if (uploadedFile.type === 'voice') {
          // Send permanent public S3 URL for storage
          requestData.audioUrl = uploadedFile.permanentUrl
          requestData.audioDisplayUrl = uploadedFile.url // presigned URL for UI display
          requestData.fileName = uploadedFile.file.name // Include file name for voice messages
          if (uploadedFile.s3Key) {
            requestData.audioS3Key = uploadedFile.s3Key
            console.log(
              '✅ [Frontend] Audio S3 Key captured:',
              uploadedFile.s3Key
            )
          } else {
            console.warn(
              '⚠️ [Frontend] No S3 Key found for uploaded audio:',
              uploadedFile
            )
          }
        }
      }

      // Ensure content is never empty - use default message if only media
      const ensuredContent =
        finalContent || (uploadedFile ? 'sent a message' : '')

      const messagePayload: {
        content: string
        type: string
        fileUrl?: string
        fileName?: string
        fileS3Key?: string
        audioUrl?: string
        audioS3Key?: string
        imageUrl?: string
        imageS3Key?: string
      } = {
        content: ensuredContent,
        type: messageType,
      }

      if (requestData.imageUrl) {
        messagePayload.imageUrl = requestData.imageUrl
        messagePayload.imageS3Key = requestData.imageS3Key
        if (requestData.fileName) {
          messagePayload.fileName = requestData.fileName
        }
      }
      if (requestData.imageS3Key) {
        messagePayload.imageS3Key = requestData.imageS3Key
      }
      if (requestData.fileUrl) {
        messagePayload.fileUrl = requestData.fileUrl
        if (requestData.fileS3Key) {
          messagePayload.fileS3Key = requestData.fileS3Key
        }
        if (requestData.fileName) {
          messagePayload.fileName = requestData.fileName
        }
      }
      if (requestData.audioUrl) {
        messagePayload.audioUrl = requestData.audioUrl
        if (requestData.audioS3Key) {
          messagePayload.audioS3Key = requestData.audioS3Key
        }
        if (requestData.fileName) {
          messagePayload.fileName = requestData.fileName
        }
      }

      console.log('📨 [Frontend] Message payload being sent:', {
        content: messagePayload.content,
        type: messagePayload.type,
        hasImageUrl: !!messagePayload.imageUrl,
        hasImageS3Key: !!messagePayload.imageS3Key,
        hasFileUrl: !!messagePayload.fileUrl,
        hasFileS3Key: !!messagePayload.fileS3Key,
        hasAudioUrl: !!messagePayload.audioUrl,
        hasAudioS3Key: !!messagePayload.audioS3Key,
        imageS3Key: messagePayload.imageS3Key,
        fileS3Key: messagePayload.fileS3Key,
        audioS3Key: messagePayload.audioS3Key,
      })

      const response = await api.agent.sendSessionMessage(
        sessionId as string,
        messagePayload
      )

      if (response.success && response.data) {
        console.log(
          '✅ Agent message sent successfully, messageId:',
          response.data.messageId,
          'agentId:',
          agentUser?.id
        )

        // Add the message immediately to UI since WebSocket will skip our own message
        const agentMessage: Message = {
          id: response.data.messageId,
          role: 'agent',
          content: finalContent,
          timestamp: new Date().toISOString(),
          agentName: agentUser.username,
          // Use display URL (presigned) for UI, S3 key for regenerating later
          imageUrl: requestData.imageDisplayUrl || requestData.imageUrl,
          imageS3Key: requestData.imageS3Key,
          audioUrl: requestData.audioDisplayUrl || requestData.audioUrl,
          audioS3Key:
            uploadedFile?.type === 'voice' ? uploadedFile.s3Key : undefined,
          fileUrl: requestData.fileDisplayUrl || requestData.fileUrl,
          fileS3Key:
            uploadedFile?.type === 'file' ? uploadedFile.s3Key : undefined,
          fileName: requestData.fileName,
        }
        setMessages(prev => {
          // Check if message with same ID already exists
          if (prev.some(msg => msg.id === agentMessage.id)) {
            console.log(
              '🔍 [DEBUG] Message with same ID already exists, skipping:',
              agentMessage.id
            )
            return prev
          }
          console.log('📤 [AGENT] Adding agent message to UI:', agentMessage)
          return [...prev, agentMessage]
        })

        // Clear uploaded file
        setUploadedFile(null)
        setSession(prev =>
          prev
            ? {
                ...prev,
                lastMessageAt: agentMessage.timestamp ?? prev.lastMessageAt,
              }
            : prev
        )
        updateAssignedSessionSidebar({
          sessionId: sessionId as string,
          preview: deriveMessagePreview({
            content: finalContent,
            imageUrl: requestData.imageUrl,
            audioUrl: requestData.audioUrl,
            fileUrl: requestData.fileUrl,
            fileName: requestData.fileName,
          }),
          timestamp: agentMessage.timestamp,
          sender: 'agent',
          role: 'agent',
          agentId:
            resolvedAgentId ??
            (token ? getAgentIdFromToken(token) : null) ??
            null,
        })
        refreshSessionSnapshot()
      }
    } catch (error) {
      console.error('Error sending message:', error)
    } finally {
      setIsSending(false)
    }
  }

  const startRecording = async () => {
    console.log('🎙️ [Recording] Start recording requested')

    if (!canReplyRef.current) {
      notificationActions.showWarning(
        'Accept handover request',
        'Accept the handover request before recording audio.'
      )
      console.log('🎙️ [Recording] Cannot reply to session')
      return
    }

    try {
      console.log('🎙️ [Recording] Requesting microphone access...')
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true })
      console.log('🎙️ [Recording] Microphone access granted, stream:', stream)

      // WhatsApp ONLY accepts these formats per their error message:
      // audio/ogg;codecs=opus, audio/mpeg, audio/amr, audio/mp4, audio/aac
      let mimeType = 'audio/webm'
      const whatsappPreferredTypes = [
        'audio/ogg;codecs=opus', // WhatsApp's preferred format
        'audio/ogg', // OGG fallback
        'audio/webm;codecs=opus', // WebM with Opus (some browsers)
        'audio/webm', // Plain WebM (fallback, may fail with WhatsApp)
      ]

      for (const type of whatsappPreferredTypes) {
        if (MediaRecorder.isTypeSupported(type)) {
          mimeType = type
          console.log(
            `🎙️ [Recording] Using WhatsApp-compatible MIME type: ${mimeType}`
          )
          break
        }
      }

      // If none are supported, try MP4
      if (
        mimeType === 'audio/webm' &&
        MediaRecorder.isTypeSupported('audio/mp4')
      ) {
        mimeType = 'audio/mp4'
        console.log(`🎙️ [Recording] Fallback to: ${mimeType}`)
      }

      const mediaRecorder = new MediaRecorder(stream, { mimeType })
      mediaRecorderRef.current = mediaRecorder
      audioChunksRef.current = []
      console.log(
        '🎙️ [Recording] MediaRecorder created with mimeType:',
        mimeType
      )

      mediaRecorder.ondataavailable = event => {
        console.log('🎙️ [Recording] Data available:', event.data.size, 'bytes')
        audioChunksRef.current.push(event.data)
      }

      mediaRecorder.onstop = async () => {
        console.log(
          '🎙️ [Recording] Recording stopped, total chunks:',
          audioChunksRef.current.length
        )
        // Use the actual mimeType from MediaRecorder for consistency
        const recordedMimeType = mediaRecorder.mimeType || 'audio/webm'
        const audioBlob = new Blob(audioChunksRef.current, {
          type: recordedMimeType,
        })
        console.log(
          '🎙️ [Recording] Audio blob created, size:',
          audioBlob.size,
          'bytes, mimeType:',
          recordedMimeType
        )

        try {
          // Upload audio with correct MIME type
          // WhatsApp accepts: audio/ogg;codecs=opus, audio/mpeg, audio/amr, audio/mp4, audio/aac
          const formData = new FormData()
          // Determine correct file extension based on MIME type
          let filename = 'voice-message.webm'
          if (recordedMimeType.includes('ogg')) {
            filename = 'voice-message.ogg'
          } else if (recordedMimeType.includes('mp4')) {
            filename = 'voice-message.mp4'
          }

          formData.append('file', audioBlob, filename)

          console.log('🎙️ [Recording] Uploading audio:', {
            mimeType: recordedMimeType,
            filename,
            blobSize: audioBlob.size,
          })
          const response = await api.upload.uploadFile(formData)
          console.log('🎙️ [Recording] Upload response:', response)

          if (response.success && response.data) {
            const uploadData = response.data as any
            // Always store the audio file, don't use transcription
            // The transcription feature was causing voice messages to be converted to text
            const audioFile = new File([audioBlob], filename, {
              type: recordedMimeType,
            })

            console.log('🎙️ [Recording] Setting uploaded file:', {
              type: 'voice',
              url: uploadData.url,
              s3Key: uploadData.s3Key,
            })

            setUploadedFile({
              url: uploadData.url,
              file: audioFile,
              type: 'voice',
              s3Key: uploadData.s3Key,
              permanentUrl: uploadData.url,
            })
          } else {
            throw new Error(response.error || 'Upload failed')
          }
        } catch (error) {
          console.error('🎙️ [Recording] Error uploading audio:', error)
          // Fall back to local audio file
          const audioUrl = URL.createObjectURL(audioBlob)
          const audioFile = new File([audioBlob], 'voice-message.webm', {
            type: recordedMimeType,
          })

          console.log('🎙️ [Recording] Using local audio blob URL:', audioUrl)
          setUploadedFile({
            url: audioUrl,
            file: audioFile,
            type: 'voice',
          })
        }

        stream.getTracks().forEach(track => track.stop())
      }

      mediaRecorder.onerror = event => {
        console.error('🎙️ [Recording] MediaRecorder error:', event.error)
      }

      console.log('🎙️ [Recording] Starting MediaRecorder...')
      mediaRecorder.start()
      setIsRecording(true)
      console.log('🎙️ [Recording] Recording started')
    } catch (error) {
      console.error('🎙️ [Recording] Error starting recording:', error)
      if (error instanceof DOMException) {
        console.error('🎙️ [Recording] DOMException details:', {
          name: error.name,
          message: error.message,
          code: error.code,
        })
      }
    }
  }

  const stopRecording = () => {
    console.log('🎙️ [Recording] Stop recording requested')
    if (mediaRecorderRef.current && isRecording) {
      console.log('🎙️ [Recording] Stopping MediaRecorder...')
      mediaRecorderRef.current.stop()
      setIsRecording(false)
      console.log('🎙️ [Recording] Recording stopped')
    } else {
      console.log('🎙️ [Recording] Cannot stop - mediaRecorder not active')
    }
  }

  const handleFileUpload = async (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    if (!canReplyRef.current) {
      notificationActions.showWarning(
        'Accept handover request',
        'Accept the handover request before uploading files.'
      )
      event.target.value = ''
      return
    }

    const file = event.target.files?.[0]
    if (file) {
      try {
        // Create a preview URL for immediate display
        const previewUrl = URL.createObjectURL(file)
        const isImage = file.type.startsWith('image/')

        // Set the file with preview URL first
        setUploadedFile({
          url: previewUrl,
          file,
          type: isImage ? 'image' : 'file',
          isUploading: true,
        })

        // Upload the file to server
        const formData = new FormData()
        formData.append('file', file)

        const response = await api.upload.uploadFile(formData)

        if (response.success && response.data) {
          const uploadData = response.data as any
          console.log('📥 [Frontend] Upload response received:', {
            type: uploadData.type,
            hasS3Key: !!uploadData.s3Key,
            s3Key: uploadData.s3Key,
            url: uploadData.url,
            presignedUrl: uploadData.presignedUrl,
          })
          if (uploadData.type === 'image' || uploadData.type === 'file') {
            // Store both the public permanent URL and presigned URL
            // - permanentUrl: Public S3 URL that never expires (use for storage)
            // - url: Presigned URL for immediate display (expires but loads faster)
            const displayUrl = uploadData.presignedUrl || uploadData.url
            const permanentUrl = uploadData.url
            setUploadedFile({
              url: displayUrl,
              file,
              type: isImage ? 'image' : 'file',
              isUploading: false,
              s3Key: uploadData.s3Key,
              permanentUrl: permanentUrl, // Store the permanent URL for sending to server
            })

            // Clean up the preview URL
            URL.revokeObjectURL(previewUrl)
          } else {
            throw new Error('Invalid upload response')
          }
        }
      } catch (error) {
        console.error('Failed to upload file:', error)
        // Keep the preview URL if upload fails
        setUploadedFile({
          url: URL.createObjectURL(file),
          file,
          type: file.type.startsWith('image/') ? 'image' : 'file',
          isUploading: false,
          uploadError: true,
        })
      }
    }
  }

  const removeUploadedFile = () => {
    if (uploadedFile) {
      URL.revokeObjectURL(uploadedFile.url)
      setUploadedFile(null)
    }
  }

  const handleTakeoverSession = useCallback(
    async (
      targetSessionId: string,
      takeoverOptions?: SessionTakeoverOptions
    ): Promise<TakeoverResult> => {
      if (!targetSessionId) {
        return {
          success: false,
          message: 'Missing session identifier',
        }
      }

      if (!token) {
        handleTokenExpiration()
        return {
          success: false,
          message: 'Authentication required',
        }
      }

      // Check if we need to show team selection modal
      // Only show if:
      // 1. Agent has multiple teams (>1)
      // 2. No teamId already provided in options
      // 3. Session has no team_id OR session's team_id is not in agent's teams
      const sessionTeamId = session?.teamId
      const needsTeamSelection =
        agentTeamIds.length > 1 &&
        !takeoverOptions?.teamId &&
        (!sessionTeamId || !agentTeamIds.includes(sessionTeamId))

      if (needsTeamSelection) {
        // Store pending takeover and show modal
        setPendingTakeoverSessionId(targetSessionId)
        setPendingTakeoverOptions(takeoverOptions)
        setSelectedTakeoverTeamId(agentTeamIds[0]) // Default to first team
        setShowTakeoverTeamModal(true)
        return {
          success: true,
          message: 'Please select a team',
        }
      }

      // Build final options with teamId
      let finalOptions = takeoverOptions ? { ...takeoverOptions } : {}

      // If session has teamId that belongs to agent, preserve it
      if (
        sessionTeamId &&
        agentTeamIds.includes(sessionTeamId) &&
        !finalOptions.teamId
      ) {
        finalOptions = {
          ...finalOptions,
          teamId: sessionTeamId,
        }
      }
      // Single team agents auto-select their team
      else if (agentTeamIds.length === 1 && !finalOptions.teamId) {
        finalOptions = {
          ...finalOptions,
          teamId: agentTeamIds[0],
        }
      }

      setIsTakeoverActionPending(true)

      try {
        const response = await api.sessions.takeoverSession(
          targetSessionId,
          finalOptions
        )

        if (!response.success) {
          throw new Error(
            response.error || 'Failed to update session assignment'
          )
        }

        const responseData = response.data ?? {}
        const serverAction = (responseData.action ||
          takeoverOptions?.action ||
          'takeover') as TakeoverActionType
        const assignmentAgentIdRaw =
          responseData.assignment?.agentId ?? responseData.agentId ?? null
        const assignmentAgentId =
          assignmentAgentIdRaw != null &&
          !Number.isNaN(Number(assignmentAgentIdRaw))
            ? Number(assignmentAgentIdRaw)
            : null
        const assignmentAgentName = responseData.assignment?.agentName
        const fallbackAgentIdRaw =
          resolvedAgentId != null
            ? resolvedAgentId
            : token
              ? getAgentIdFromToken(token)
              : null
        const currentAgentId =
          fallbackAgentIdRaw != null &&
          !Number.isNaN(Number(fallbackAgentIdRaw))
            ? Number(fallbackAgentIdRaw)
            : null
        const isAssignedToCurrentAgent =
          assignmentAgentId != null &&
          currentAgentId != null &&
          assignmentAgentId === currentAgentId

        const shouldSendSystemMessage =
          isAssignedToCurrentAgent &&
          (serverAction === 'takeover' || serverAction === 'force_takeover')

        const fallbackMessages: Record<TakeoverActionType, string> = {
          takeover: `You are now handling session ${targetSessionId.slice(-8).toUpperCase()}`,
          force_takeover: `You are now handling session ${targetSessionId.slice(-8).toUpperCase()}`,
          assign_agent: `Session ${targetSessionId.slice(-8).toUpperCase()} assigned to ${assignmentAgentName || 'the selected agent'}.`,
          assign_team: `Session ${targetSessionId.slice(-8).toUpperCase()} returned to the team queue.`,
        }

        const successMessage =
          typeof responseData.message === 'string' &&
          responseData.message.length
            ? responseData.message
            : fallbackMessages[serverAction]

        if (shouldSendSystemMessage) {
          try {
            const systemMessage = {
              content: `An agent has joined the chat to help you. Hi, I'm ${agentUser?.username || 'an agent'}!`,
              type: 'text',
              role: 'system',
            }
            await api.agent.sendSessionMessage(targetSessionId, systemMessage)
          } catch (error) {
            console.error(
              'Failed to send system message after takeover:',
              error
            )
          }
        }

        if (serverAction === 'assign_agent' && !isAssignedToCurrentAgent) {
          notificationActions.showSuccess('Session Reassigned', successMessage)
        } else if (serverAction === 'assign_team') {
          notificationActions.showSuccess('Session Released', successMessage)
        } else {
          notificationActions.showSuccess('Session Taken Over', successMessage)
        }

        setIsAssignedToMe(isAssignedToCurrentAgent)

        await checkSessionAssignment()
        await refreshSessionSnapshot()
        await checkPendingTransfer()

        if (
          shouldSendSystemMessage ||
          (isAssignedToCurrentAgent &&
            (serverAction === 'assign_agent' ||
              serverAction === 'force_takeover'))
        ) {
          setTimeout(() => {
            if (messagesContainerRef.current) {
              messagesContainerRef.current.scrollTop =
                messagesContainerRef.current.scrollHeight
            }
          }, 300)
        }

        return {
          success: true,
          message: successMessage,
        }
      } catch (error) {
        console.error('Error taking over session:', error)

        const messageText =
          error instanceof Error && error.message
            ? error.message
            : 'Failed to update session assignment'

        if (messageText.toLowerCase().includes('authentication required')) {
          handleTokenExpiration()
        } else if (messageText.toLowerCase().includes('already assigned')) {
          notificationActions.showWarning(
            'Session Already Assigned',
            messageText
          )
        } else {
          notificationActions.showError('Takeover Failed', messageText)
        }

        return {
          success: false,
          message: messageText,
        }
      } finally {
        setIsTakeoverActionPending(false)
      }
    },
    [
      agentTeamIds,
      agentUser?.username,
      checkPendingTransfer,
      checkSessionAssignment,
      handleTokenExpiration,
      notificationActions,
      refreshSessionSnapshot,
      resolvedAgentId,
      session?.teamId,
      token,
    ]
  )

  const openSupervisorManagement = useCallback(() => {
    setSupervisorActionError(null)
    setIsSupervisorModalOpen(true)
  }, [])

  const handleSupervisorSubmit = useCallback(
    async ({ teamId, agentId, notes }: SupervisorManageDialogSubmit) => {
      if (!sessionId) {
        return
      }

      setSupervisorActionError(null)
      setIsSupervisorActionLoading(true)
      try {
        const reason =
          notes && notes.trim().length > 0 ? notes.trim() : undefined

        const isAlreadyAssignedMessage = (message?: string | null) =>
          typeof message === 'string' &&
          message.toLowerCase().includes('already assigned')

        const assignAgent = () =>
          handleTakeoverSession(sessionId, {
            action: 'assign_agent',
            targetAgentId: agentId as number,
            reason,
            teamId,
          })

        const releaseToTeam = () => {
          if (teamId == null) {
            return Promise.resolve({
              success: false,
              message: 'Select a team before assigning.',
            })
          }
          return releaseSessionToTeam(sessionId, teamId, reason)
        }

        let takeoverResult: TakeoverResult

        if (agentId != null) {
          takeoverResult = await assignAgent()

          if (
            !takeoverResult.success &&
            isAlreadyAssignedMessage(takeoverResult.message)
          ) {
            const releaseResult = await releaseToTeam()

            if (!releaseResult.success) {
              const fallback = 'Unable to update session assignment.'
              setSupervisorActionError(
                releaseResult.message && releaseResult.message.trim().length > 0
                  ? releaseResult.message
                  : fallback
              )
              return
            }

            const normalizedTeamId =
              teamId != null && Number.isFinite(teamId) ? teamId : null
            applySupervisorAssignmentSnapshot(sessionId, normalizedTeamId)

            takeoverResult = await assignAgent()
          }
        } else {
          const releaseResult = await releaseToTeam()
          if (!releaseResult.success) {
            const fallback = 'Unable to update session assignment.'
            setSupervisorActionError(
              releaseResult.message && releaseResult.message.trim().length > 0
                ? releaseResult.message
                : fallback
            )
            return
          }

          takeoverResult = releaseResult
        }

        if (takeoverResult.success) {
          const normalizedTeamId =
            teamId != null && Number.isFinite(teamId) ? teamId : null
          applySupervisorAssignmentSnapshot(sessionId, normalizedTeamId)
          setIsSupervisorModalOpen(false)
          setSupervisorActionError(null)
        } else {
          const fallback = 'Unable to update session assignment.'
          setSupervisorActionError(
            takeoverResult.message && takeoverResult.message.trim().length > 0
              ? takeoverResult.message
              : fallback
          )
        }
      } finally {
        setIsSupervisorActionLoading(false)
      }
    },
    [
      applySupervisorAssignmentSnapshot,
      handleTakeoverSession,
      releaseSessionToTeam,
      sessionId,
    ]
  )

  const completeSession = async () => {
    try {
      if (!token) {
        handleTokenExpiration()
        return
      }

      const response = await api.sessions.completeSession(sessionId as string)

      if (!response.success) {
        throw new Error(response.error || 'Failed to complete session')
      }

      // Show success toast notification
      notificationActions.showSuccess(
        'Session Completed',
        'The session has been successfully completed.'
      )

      // Navigate back to dashboard (notification will be sent by server)
      handleExitToDashboard()
    } catch (error) {
      console.error('Error completing session:', error)

      // Handle authorization errors
      if (error && typeof error === 'object' && 'status' in error) {
        const errorObj = error as any
        if (errorObj.status === 403) {
          const errorData = errorObj.data
          notificationActions.showError(
            'Access Denied',
            `Only ${errorData?.assignedTo || 'the assigned agent'} can complete this session.`
          )
          handleExitToDashboard()
          return
        }
        if (errorObj.status === 404) {
          notificationActions.showError(
            'Session Not Found',
            'This session is no longer active.'
          )
          handleExitToDashboard()
          return
        }
      }

      notificationActions.showError(
        'Failed to Complete Session',
        'Unable to complete the session. Please try again.'
      )
    }
  }

  const handleCancelTransfer = async () => {
    if (!pendingTransfer || !sessionId) return
    if (!canTransfer) {
      notificationActions.showError(
        'Permission denied',
        'You do not have access to manage transfers.'
      )
      return
    }

    try {
      const response = await api.sessions.cancelTransfer(
        sessionId as string,
        pendingTransfer.id
      )

      if (response.success) {
        setPendingTransfer(null)
        loadSession() // Refresh session data
        notificationActions.showSuccess(
          'Transfer Cancelled',
          'The transfer request has been cancelled.'
        )
      } else {
        const message =
          response.error?.trim() ||
          response.message?.trim() ||
          'Failed to cancel transfer request.'
        notificationActions.showError('Cancel Transfer Failed', message)
      }
    } catch (error) {
      console.error('Error cancelling transfer:', error)
      const message =
        error instanceof Error && error.message
          ? error.message
          : 'Failed to cancel transfer request.'
      notificationActions.showError('Cancel Transfer Failed', message)
    }
  }

  const handleAcceptTransfer = async () => {
    if (!pendingTransfer || !sessionId) return
    if (!canTransfer) {
      notificationActions.showError(
        'Permission denied',
        'You do not have access to accept transfers.'
      )
      return
    }
    if (!pendingTransferAssignedToCurrentAgent && !isCurrentAgentInTargetTeam) {
      notificationActions.showWarning(
        'Transfer Not Assigned',
        'This transfer is not assigned to you.'
      )
      return
    }

    try {
      setIsAcceptingTransfer(true)

      const payload: {
        response: 'accepted'
        teamId?: number
      } = { response: 'accepted' }

      if (
        typeof pendingTransfer.teamId === 'number' &&
        Number.isFinite(pendingTransfer.teamId)
      ) {
        payload.teamId = pendingTransfer.teamId
      }

      const response = await api.agent.respondToHandoverAssignment(
        pendingTransfer.id,
        payload
      )

      if (!response.success) {
        // Check if the error is about session already being assigned
        if (response.error?.includes('already been assigned')) {
          notificationActions.showWarning(
            'Session Already Assigned',
            response.error
          )
          // Refresh the pending transfer to update UI state
          await checkPendingTransfer()
        } else {
          throw new Error(response.error || 'Failed to accept transfer')
        }
      } else {
        notificationActions.showSuccess(
          'Transfer Accepted',
          'You are now handling this session.'
        )

        await Promise.all([
          loadSession(),
          checkSessionAssignment(),
          refreshSessionSnapshot(),
        ])
        await checkPendingTransfer()
      }
    } catch (error) {
      console.error('Error accepting transfer:', error)
      const message =
        error instanceof Error && error.message
          ? error.message
          : 'Failed to accept transfer'
      notificationActions.showError('Accept Failed', message)
    } finally {
      setIsAcceptingTransfer(false)
    }
  }

  const handleConfirmTakeoverTeamSelection = async () => {
    if (!pendingTakeoverSessionId || selectedTakeoverTeamId === null) {
      return
    }

    setShowTakeoverTeamModal(false)

    // Build options with selected team
    const optionsWithTeam: SessionTakeoverOptions = {
      ...pendingTakeoverOptions,
      teamId: selectedTakeoverTeamId,
    }

    // Clear pending state
    const sessionId = pendingTakeoverSessionId
    const options = optionsWithTeam
    setPendingTakeoverSessionId(null)
    setPendingTakeoverOptions(undefined)
    setSelectedTakeoverTeamId(null)

    // Now actually perform the takeover with the selected team
    await handleTakeoverSession(sessionId, options)
  }

  const handleTransferComplete = () => {
    // Refresh session data to reflect the transfer
    loadSession()
    checkPendingTransfer()
    // Don't navigate away immediately, let user see the result
  }

  const getPlatformIcon = (platform: string) => {
    switch (platform) {
      case 'whatsapp':
        return <Phone className="h-4 w-4" />
      case 'facebook':
        return <Smartphone className="h-4 w-4" />
      default:
        return <Monitor className="h-4 w-4" />
    }
  }

  const sessionMetadata = useMemo<Record<string, any>>(
    () =>
      session &&
      typeof session.metadata === 'object' &&
      session.metadata !== null
        ? (session.metadata as Record<string, any>)
        : {},
    [session]
  )

  const resolveMetadataValue = useCallback(
    (paths: string[]): unknown => {
      for (const rawPath of paths) {
        const segments = rawPath
          .replace(/\[(\d+)\]/g, '.$1')
          .split('.')
          .filter(Boolean)

        let current: any = sessionMetadata
        let found = true

        for (const segment of segments) {
          if (Array.isArray(current) && /^\d+$/.test(segment)) {
            const index = Number(segment)
            current =
              index >= 0 && index < current.length ? current[index] : undefined
          } else if (
            current &&
            typeof current === 'object' &&
            Object.prototype.hasOwnProperty.call(current, segment)
          ) {
            current = (current as Record<string, unknown>)[segment]
          } else {
            found = false
            break
          }

          if (current === undefined || current === null) {
            found = false
            break
          }
        }

        if (!found) {
          continue
        }

        if (typeof current === 'string' && current.trim().length === 0) {
          continue
        }

        return current
      }

      return undefined
    },
    [sessionMetadata]
  )

  const resolveContactField = useCallback(
    (paths: string[]) =>
      toDisplayString(resolveMetadataValue(paths)) ?? undefined,
    [resolveMetadataValue]
  )

  const metadataEmail = resolveContactField([
    'contact.email',
    'user.email',
    'visitor.email',
    'customer.email',
    'profile.email',
    'account.email',
    'email',
    'contactEmail',
  ])

  const metadataPhone = resolveContactField([
    'contact.phoneNumber',
    'contact.phone',
    'contact.phone_number',
    'user.phoneNumber',
    'user.phone',
    'visitor.phone',
    'customer.phone',
    'profile.phone',
    'contact.phoneNumberFormatted',
    'contactAddress',
    'contactPhone',
    'whatsappSenderId',
    'phoneNumber',
    'phone',
  ])

  const metadataWhatsappId =
    resolveContactField([
      'contact.whatsappId',
      'contact.whatsappNumber',
      'contact.waId',
      'whatsappId',
      'waId',
      'whatsappSenderId',
      'contactAddress',
    ]) ?? metadataPhone

  const metadataMessengerId = resolveContactField([
    'contact.fbMessengerId',
    'contact.messengerId',
    'contact.facebookId',
    'fbMessengerId',
    'messengerId',
    'facebookId',
    'facebookSenderId',
  ])

  const metadataTelegramId = resolveContactField([
    'contact.telegramUserId',
    'contact.telegramId',
    'telegramUserId',
    'telegramId',
    'telegramUsername',
    'telegramChatId',
    'telegram.username',
  ])

  const metadataTelegramUserId = resolveContactField([
    'contact.telegramUserId',
    'telegramUserId',
    'telegram.userId',
    'telegram.user.id',
  ])

  const metadataInstagramId = resolveContactField([
    'contact.instagramId',
    'instagramId',
    'instagramSenderId',
  ])

  const metadataWeChatId = resolveContactField([
    'contact.weChatId',
    'contact.wechatId',
    'weChatId',
    'wechatId',
  ])

  const metadataLineId = resolveContactField(['contact.lineId', 'lineId'])

  const sessionPlatform = session?.platform ?? null
  const sessionPlatformId = session?.platformId ?? null
  const sessionUserId = session?.userId ?? null

  const contactLookupIdentifiers = useMemo(
    () =>
      deriveSessionContactIdentifiers({
        platform: sessionPlatform,
        platformId: sessionPlatformId,
        userId: sessionUserId,
        identifiers: [
          metadataEmail,
          metadataPhone,
          metadataWhatsappId,
          metadataMessengerId,
          metadataTelegramId,
          metadataTelegramUserId,
          metadataInstagramId,
          metadataWeChatId,
          metadataLineId,
        ],
      }),
    [
      sessionPlatform,
      sessionPlatformId,
      sessionUserId,
      metadataEmail,
      metadataPhone,
      metadataWhatsappId,
      metadataMessengerId,
      metadataTelegramId,
      metadataTelegramUserId,
      metadataInstagramId,
      metadataWeChatId,
      metadataLineId,
    ]
  )

  const { data: contactMatchesMap } = useAgentContactMatches(
    contactLookupIdentifiers
  )

  const contactMatch = useMemo(() => {
    if (!contactMatchesMap || !contactLookupIdentifiers.length) {
      return undefined
    }

    for (const identifier of contactLookupIdentifiers) {
      const normalized = normalizeForComparison(identifier)
      if (!normalized) {
        continue
      }

      const match = contactMatchesMap[normalized]
      if (match) {
        return match
      }
    }

    return undefined
  }, [contactMatchesMap, contactLookupIdentifiers])

  const contactDisplayName = contactMatch
    ? getContactDisplayName(contactMatch)
    : undefined

  const resolvedContactId = useMemo(() => {
    if (
      !contactMatch ||
      contactMatch.id === null ||
      contactMatch.id === undefined
    ) {
      return null
    }
    const numeric = Number(contactMatch.id)
    return Number.isFinite(numeric) ? numeric : null
  }, [contactMatch])

  const metadataUsername =
    toDisplayString(
      resolveMetadataValue([
        'user.username',
        'user.name',
        'user.displayName',
        'visitor.username',
        'visitor.name',
        'profile.username',
        'profile.name',
        'customer.name',
        'customer.username',
        'contact.name',
        'contact.fullName',
        'account.name',
      ])
    ) ?? undefined

  const sessionContactDefaults = useMemo<ContactFormValues>(() => {
    const fallbackName =
      session?.displayName ?? contactDisplayName ?? metadataUsername ?? ''
    const trimmed = fallbackName.trim()
    let firstName = ''
    let lastName = ''
    if (trimmed) {
      const parts = trimmed.split(/\s+/)
      firstName = parts[0] ?? ''
      lastName = parts.slice(1).join(' ')
    }

    const firstNonEmpty = (
      ...values: Array<string | null | undefined>
    ): string => {
      for (const value of values) {
        const candidate = value?.trim()
        if (candidate) {
          return candidate
        }
      }
      return ''
    }

    const platformLower = session?.platform
      ? session.platform.toLowerCase()
      : ''
    const sessionIdValue =
      typeof session?.id === 'string' ? session.id : undefined
    const sessionUserIdValue =
      session?.userId != null ? String(session.userId) : undefined

    const whatsappSenderId = toDisplayString(
      resolveMetadataValue(['whatsappSenderId'])
    )
    const facebookSenderId = toDisplayString(
      resolveMetadataValue(['facebookSenderId'])
    )
    const contactAddress = toDisplayString(
      resolveMetadataValue(['contactAddress'])
    )
    const telegramChatId = toDisplayString(
      resolveMetadataValue(['telegramChatId'])
    )
    const telegramUsername = toDisplayString(
      resolveMetadataValue(['telegramUsername'])
    )
    const instagramSenderId = toDisplayString(
      resolveMetadataValue(['instagramSenderId'])
    )
    const telegramNumericId = toDisplayString(
      resolveMetadataValue(['telegramUserId'])
    )

    const phoneNumber = firstNonEmpty(
      metadataPhone,
      metadataWhatsappId,
      platformLower === 'whatsapp' ? whatsappSenderId : undefined,
      platformLower === 'sms' || platformLower === 'twilio'
        ? (contactAddress ?? sessionUserIdValue)
        : undefined,
      platformLower === 'whatsapp' ? sessionUserIdValue : undefined
    )

    const fbMessengerId = firstNonEmpty(
      metadataMessengerId,
      platformLower === 'facebook' || platformLower === 'facebook_messenger'
        ? facebookSenderId
        : undefined,
      platformLower === 'facebook' || platformLower === 'facebook_messenger'
        ? sessionIdValue
        : undefined
    )

    const telegramId = firstNonEmpty(
      metadataTelegramId,
      platformLower === 'telegram' ? telegramUsername : undefined,
      platformLower === 'telegram' ? telegramChatId : undefined
    )

    const telegramUserId = firstNonEmpty(
      metadataTelegramUserId,
      platformLower === 'telegram' ? telegramNumericId : undefined,
      platformLower === 'telegram' ? sessionUserIdValue : undefined,
      platformLower === 'telegram' ? sessionIdValue : undefined
    )

    const instagramId = firstNonEmpty(
      metadataInstagramId,
      platformLower === 'instagram' ? instagramSenderId : undefined,
      platformLower === 'instagram' ? sessionIdValue : undefined
    )

    return {
      firstName,
      lastName,
      title: '',
      email: metadataEmail ?? '',
      phoneNumber,
      fbMessengerId,
      telegramId,
      telegramUserId,
      instagramId,
      weChatId: metadataWeChatId ?? '',
      lineId: metadataLineId ?? '',
    }
  }, [
    session?.displayName,
    session?.platform,
    session?.id,
    session?.userId,
    contactDisplayName,
    metadataUsername,
    metadataEmail,
    metadataPhone,
    metadataWhatsappId,
    metadataMessengerId,
    metadataTelegramId,
    metadataTelegramUserId,
    metadataInstagramId,
    metadataWeChatId,
    metadataLineId,
    resolveMetadataValue,
  ])

  const contactForm = useContactForm({
    sessionContactDefaults,
    contactMatch,
    resolvedContactId,
  })

  const contactIdDisplay =
    contactMatch && contactMatch.id !== null && contactMatch.id !== undefined
      ? String(contactMatch.id)
      : null

  // Show loading while authentication is being verified
  if (authIsLoading || (!isAuthenticated && !agentUser)) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto" />
          <p className="mt-4 text-gray-600">Authenticating...</p>
        </div>
      </div>
    )
  }

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto" />
          <p className="mt-4 text-gray-600">Loading session...</p>
        </div>
      </div>
    )
  }

  if (!session) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <p className="text-gray-600">Session not found</p>
          <button
            onClick={() => handleExitToDashboard()}
            className="mt-4 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
          >
            Back to Dashboard
          </button>
        </div>
      </div>
    )
  }

  const isWebPlatform = session.platform === 'web'
  // CHAT TAB: Use chat_sessions table as single source of truth for assignment
  // sessionAssignment is ONLY used for pending handover requests (accept banner)
  const sessionAssignment =
    session.assignment && typeof session.assignment === 'object'
      ? session.assignment
      : null

  // Primary source: chat_sessions table fields (session.agentId, session.teamId)
  const assignedAgentIdRaw = session.agentId ?? null
  const assignedAgentId =
    assignedAgentIdRaw != null && !Number.isNaN(Number(assignedAgentIdRaw))
      ? Number(assignedAgentIdRaw)
      : null
  const resolvedCurrentAgentId =
    agentId != null && !Number.isNaN(Number(agentId)) ? Number(agentId) : null

  // Use session.agentName from chat_sessions, no fallback to sessionAssignment
  const assignedAgentNameRaw =
    typeof session.agentName === 'string' && session.agentName.trim().length > 0
      ? session.agentName.trim()
      : undefined
  const assignedAgentName = assignedAgentNameRaw ?? undefined

  // For handover request acceptance (banner display only)
  const assignmentStatusRaw =
    typeof sessionAssignment?.status === 'string'
      ? sessionAssignment.status
      : null
  const normalizedAssignmentStatus = assignmentStatusRaw
    ? assignmentStatusRaw.toUpperCase()
    : null
  const assignmentRequestId = toNullableNumber(
    sessionAssignment?.requestId ?? null
  )
  const assignmentAgentId = toNullableNumber(sessionAssignment?.agentId ?? null)
  const assignmentTeamId = toNullableNumber(sessionAssignment?.teamId ?? null)
  const assignmentRequestType = sessionAssignment?.requestType ?? null
  const assignmentAssignedBy = toNullableNumber(
    sessionAssignment?.assignedBy ?? null
  )
  const assignmentTeamName = sessionAssignment?.teamName ?? null

  // Check if current agent is the initiator (for transfers)
  const isTransferInitiator =
    assignmentRequestType === 'agent_transfer' &&
    assignmentAssignedBy != null &&
    resolvedCurrentAgentId != null &&
    assignmentAssignedBy === resolvedCurrentAgentId

  // Check if current agent can accept this handover assignment
  // Scenarios:
  // 1. Direct assignment: assignmentAgentId matches current agent
  // 2. Team broadcast: assignmentAgentId is null but assignmentTeamId matches one of agent's teams
  // 3. Global broadcast: both assignmentAgentId and assignmentTeamId are null (all agents can accept)
  const isDirectlyAssignedToCurrentAgent =
    assignmentAgentId != null &&
    resolvedCurrentAgentId != null &&
    assignmentAgentId === resolvedCurrentAgentId

  const isAssignedToCurrentAgentTeam =
    assignmentAgentId == null &&
    assignmentTeamId != null &&
    agentTeamIds.includes(assignmentTeamId)

  const isGlobalBroadcast =
    assignmentAgentId == null && assignmentTeamId == null

  // Check if assignment is already taken by someone else
  const isAssignedToSomeoneElse =
    normalizedAssignmentStatus === 'ASSIGNED' &&
    assignmentAgentId != null &&
    resolvedCurrentAgentId != null &&
    assignmentAgentId !== resolvedCurrentAgentId

  const isAssignmentPendingForCurrentAgent = Boolean(
    normalizedAssignmentStatus === 'PENDING' &&
      assignmentRequestId != null &&
      resolvedCurrentAgentId != null &&
      !isAssignedToSomeoneElse &&
      !isTransferInitiator && // Exclude transfer initiator from seeing acceptance banner
      (isDirectlyAssignedToCurrentAgent ||
        isAssignedToCurrentAgentTeam ||
        isGlobalBroadcast)
  )
  const canReplyToSession =
    isAssignedToMe && !isAssignmentPendingForCurrentAgent
  canReplyRef.current = canReplyToSession

  // For completed sessions, treat as unassigned (hide historical assignment for all agents)
  const sessionIsCompleted = session.status === 'completed'
  const displayAgentName = sessionIsCompleted ? null : assignedAgentName

  // For transfers, show special labels
  const isTransfer = assignmentRequestType === 'agent_transfer'
  const transferPrefix = isTransfer ? `Transfer Agent - ` : ''
  const teamTransferInfo =
    isTransfer && assignmentTeamName
      ? ` (Transfer Team - ${assignmentTeamName})`
      : ''

  const assignedAgentBadgeLabel = sessionIsCompleted
    ? 'Unassigned'
    : displayAgentName
      ? `${isTransferInitiator ? 'Transferred to ' : transferPrefix}${displayAgentName}${isAssignedToMe ? ' (You)' : ''}${teamTransferInfo}${isAssignmentPendingForCurrentAgent ? ' – awaiting acceptance' : ''}`
      : isAssignedToMe
        ? 'Assigned to you'
        : 'Unassigned'
  const assignmentStatusDisplay =
    session.status === 'completed'
      ? 'Completed'
      : assignmentStatusRaw
        ? normalizedAssignmentStatus === 'PENDING'
          ? 'Pending acceptance'
          : normalizedAssignmentStatus === 'ASSIGNED'
            ? 'Accepted'
            : assignmentStatusRaw
                .split('_')
                .map(word =>
                  word.length > 0
                    ? word[0].toUpperCase() + word.slice(1).toLowerCase()
                    : word
                )
                .join(' ')
        : 'Not assigned'
  const assignmentRequestedAtDisplay =
    typeof sessionAssignment?.requestedAt === 'string' &&
    sessionAssignment.requestedAt.trim().length > 0
      ? formatDateTime(sessionAssignment.requestedAt)
      : null
  const shouldShowAssignmentAcceptanceBanner = Boolean(
    isAssignmentPendingForCurrentAgent && assignmentRequestId != null
  )
  const canAcceptCurrentAssignment = shouldShowAssignmentAcceptanceBanner

  const handleAcceptSessionAssignment = async () => {
    if (!assignmentRequestId || !sessionId) {
      return
    }

    try {
      setIsAcceptingAssignment(true)
      const response = await api.agent.respondToHandoverAssignment(
        assignmentRequestId,
        {
          response: 'accepted',
          // Pass the teamId from the assignment to preserve it
          ...(assignmentTeamId != null && { teamId: assignmentTeamId }),
        }
      )

      if (!response.success) {
        const message =
          response.error?.trim() ||
          response.message?.trim() ||
          'Failed to accept handover request.'
        notificationActions.showError('Accept Failed', message)
        return
      }

      notificationActions.showSuccess(
        'Handover Accepted',
        'You can now reply to the guest.'
      )

      await Promise.all([
        loadSession(),
        checkSessionAssignment(),
        refreshSessionSnapshot(),
      ])
      await checkPendingTransfer()
    } catch (error) {
      console.error('Error accepting handover assignment:', error)
      const message =
        error instanceof Error && error.message
          ? error.message
          : 'Failed to accept handover request.'
      notificationActions.showError('Accept Failed', message)
    } finally {
      setIsAcceptingAssignment(false)
    }
  }

  // Agent assignment display - simplified for Chat tab
  const assignedAgentInfoValue = sessionIsCompleted
    ? 'Unassigned'
    : assignedAgentId != null && displayAgentName
      ? `${isTransferInitiator ? 'Transferred to ' : transferPrefix}${displayAgentName}${isAssignedToMe ? ' (You)' : ''}${teamTransferInfo}${isAssignmentPendingForCurrentAgent ? ' – awaiting acceptance' : ''}`
      : assignedAgentId != null && isAssignedToMe
        ? 'Assigned to you'
        : 'Unassigned'
  // Team assignment - use chat_sessions table only (session.teamId, session.teamName)
  const assignedTeamIdRaw = session.teamId ?? null
  const assignedTeamId =
    assignedTeamIdRaw != null && !Number.isNaN(Number(assignedTeamIdRaw))
      ? Number(assignedTeamIdRaw)
      : null
  const assignedTeamNameRaw =
    typeof session.teamName === 'string' && session.teamName.trim().length > 0
      ? session.teamName.trim()
      : null
  const assignedTeamName =
    assignedTeamNameRaw ??
    (assignedTeamId != null ? (getTeamNameById(assignedTeamId) ?? null) : null)

  // Team display - simplified
  const assignedTeamInfoValue =
    assignedTeamName ??
    (assignedTeamId != null ? `#${assignedTeamId}` : 'Unassigned')
  const assignedTeamBadgeLabel =
    assignedTeamName != null
      ? `${assignedTeamName}`
      : assignedTeamId != null
        ? `#${assignedTeamId}`
        : 'Unassigned'

  // Session assignment state - treat completed sessions as available/unassigned for action controls
  const isSessionAssigned = assignedAgentId != null
  const isAssignedToAnotherAgent =
    assignedAgentId != null &&
    !isAssignedToMe &&
    (resolvedCurrentAgentId == null ||
      assignedAgentId !== resolvedCurrentAgentId)
  const isSessionAvailable = sessionIsCompleted || !isSessionAssigned
  const pendingTransferAgentLabel =
    pendingTransfer?.assignedToAgentName &&
    pendingTransfer.assignedToAgentName.trim().length > 0
      ? pendingTransfer.assignedToAgentName.trim()
      : pendingTransfer?.assignedToAgentUsername &&
          pendingTransfer.assignedToAgentUsername.trim().length > 0
        ? pendingTransfer.assignedToAgentUsername.trim()
        : null
  const pendingTransferTeamLabel =
    pendingTransfer?.teamName && pendingTransfer.teamName.trim().length > 0
      ? pendingTransfer.teamName.trim()
      : pendingTransfer?.teamId != null
        ? (getTeamNameById(pendingTransfer.teamId) ??
          `Team #${pendingTransfer.teamId}`)
        : null
  const pendingTransferTargetLabel =
    pendingTransferTeamLabel ?? pendingTransferAgentLabel
  const pendingTransferReason =
    pendingTransfer?.reason && pendingTransfer.reason.trim().length > 0
      ? pendingTransfer.reason
      : 'No reason provided'
  const pendingTransferReasonDetail =
    pendingTransfer?.reason && pendingTransfer.reason.trim().length > 0
      ? pendingTransfer.reason.trim()
      : null
  const pendingTransferStatus =
    typeof pendingTransfer?.status === 'string'
      ? pendingTransfer.status.toUpperCase()
      : null
  const pendingTransferStatusLabel =
    pendingTransferStatus === 'ASSIGNED' ? 'Assigned' : 'Pending'
  const pendingTransferRequestedAt =
    pendingTransfer?.requestedAt && pendingTransfer.requestedAt.length > 0
      ? formatDateTime(pendingTransfer.requestedAt)
      : null
  const pendingTransferAssignedToCurrentAgent =
    pendingTransfer?.assignedToAgentId != null &&
    resolvedCurrentAgentId != null &&
    pendingTransfer.assignedToAgentId === resolvedCurrentAgentId &&
    (pendingTransferStatus === 'PENDING' ||
      pendingTransferStatus === 'ASSIGNED')
  const isCurrentAgentInTargetTeam =
    pendingTransfer?.teamId != null &&
    agentTeamIds.includes(pendingTransfer.teamId)
  const transferRequiresAcceptance = Boolean(
    pendingTransfer &&
      pendingTransferStatus === 'PENDING' &&
      (pendingTransferAssignedToCurrentAgent || isCurrentAgentInTargetTeam)
  )
  const shouldShowTransferAcceptanceBanner =
    transferRequiresAcceptance && !isAssignedToMe
  const canAcceptPendingTransfer =
    pendingTransfer &&
    pendingTransferStatus !== 'COMPLETED' &&
    (pendingTransferAssignedToCurrentAgent || isCurrentAgentInTargetTeam)
  const acceptanceBannerMode: 'handover' | 'transfer' | null =
    shouldShowAssignmentAcceptanceBanner
      ? 'handover'
      : shouldShowTransferAcceptanceBanner
        ? 'transfer'
        : null
  const acceptanceBannerConfig =
    acceptanceBannerMode === 'handover'
      ? {
          title: 'Accept handover to reply',
          description:
            'This conversation is assigned to you but still pending acceptance. Accept the handover request before replying to the guest.',
          meta: assignmentRequestedAtDisplay
            ? `Requested ${assignmentRequestedAtDisplay}`
            : null,
          buttonLabel: isAcceptingAssignment ? 'Accepting...' : 'Accept',
          onClick: handleAcceptSessionAssignment,
          buttonDisabled: !canAcceptCurrentAssignment || isAcceptingAssignment,
        }
      : acceptanceBannerMode === 'transfer'
        ? {
            title: 'Accept transfer to reply',
            description: pendingTransferTeamLabel
              ? `This conversation was transferred to ${pendingTransferTeamLabel}. Accept the transfer to take over.`
              : pendingTransferAgentLabel
                ? `${pendingTransferAgentLabel} requested to transfer this conversation. Accept the transfer to take over.`
                : 'Another agent requested to transfer this conversation. Accept the transfer to take over.',
            meta: pendingTransferRequestedAt
              ? `Requested ${pendingTransferRequestedAt}`
              : null,
            extra: pendingTransferReasonDetail
              ? `Reason: ${pendingTransferReasonDetail}`
              : null,
            buttonLabel: isAcceptingTransfer
              ? 'Accepting...'
              : 'Accept transfer',
            onClick: handleAcceptTransfer,
            buttonDisabled: !canAcceptPendingTransfer || isAcceptingTransfer,
          }
        : null
  const canCancelPendingTransfer = Boolean(
    canTransfer &&
      isAssignedToMe &&
      !pendingTransferAssignedToCurrentAgent &&
      !isCurrentAgentInTargetTeam
  )
  const pendingTransferDescription = canAcceptPendingTransfer
    ? 'This transfer is assigned to you. Accept the request to take over the conversation.'
    : pendingTransferTeamLabel
      ? `Waiting for ${pendingTransferTeamLabel} to take ownership.`
      : pendingTransferAgentLabel
        ? `Waiting for ${pendingTransferAgentLabel} to accept.`
        : 'Awaiting assignment to an agent.'
  const shouldShowPendingTransferCard = Boolean(
    pendingTransfer &&
      (pendingTransferAssignedToCurrentAgent ||
        isCurrentAgentInTargetTeam ||
        canCancelPendingTransfer)
  )

  const userAgentString =
    toDisplayString(
      resolveMetadataValue([
        'userAgent',
        'headers.user-agent',
        'context.userAgent',
        'browser.userAgent',
        'navigator.userAgent',
        'client.userAgent',
        'ua',
      ])
    ) ?? undefined

  const metadataUserId =
    toDisplayString(
      resolveMetadataValue([
        'user.id',
        'userId',
        'visitor.id',
        'visitorId',
        'customer.id',
        'profile.id',
        'contact.id',
        'account.id',
      ])
    ) ?? undefined

  const resolvedUserId = session.userId ?? metadataUserId ?? undefined

  // Use displayName from backend if available, otherwise fall back to metadata or Guest
  const userDisplay = session.displayName
    ? session.displayName
    : contactDisplayName
      ? resolvedUserId
        ? `${contactDisplayName} (ID: ${resolvedUserId})`
        : contactDisplayName
      : metadataUsername && resolvedUserId
        ? `${metadataUsername} (ID: ${resolvedUserId})`
        : (metadataUsername ?? resolvedUserId ?? 'Guest')

  const userSenderLabel =
    session.displayName ?? contactDisplayName ?? metadataUsername ?? 'Guest'

  const createdAtDisplay = formatDateTime(session.createdAt)

  const lastMessageTimestamp =
    session.lastMessageAt ??
    (messages.length > 0 ? messages[messages.length - 1].timestamp : undefined)
  const lastMessageDisplay = lastMessageTimestamp
    ? formatDateTime(lastMessageTimestamp)
    : 'No messages yet'

  const browserNameCandidate =
    toDisplayString(
      resolveMetadataValue([
        'browser.name',
        'browser',
        'client.browser.name',
        'client.browser',
        'navigator.browser',
      ])
    ) ?? undefined

  const browserVersion =
    toDisplayString(
      resolveMetadataValue([
        'browser.version',
        'client.browser.version',
        'browserVersion',
      ])
    ) ?? undefined

  const resolvedBrowserName =
    browserNameCandidate ??
    (userAgentString ? inferBrowserFromUserAgent(userAgentString) : undefined)

  const browserDisplay =
    resolvedBrowserName && browserVersion
      ? `${resolvedBrowserName} ${browserVersion}`
      : (resolvedBrowserName ?? browserVersion ?? 'Unknown')

  const osCandidate =
    toDisplayString(
      resolveMetadataValue([
        'os.name',
        'os',
        'device.os',
        'client.os',
        'navigator.platform',
      ])
    ) ?? undefined

  const resolveOsDisplay = (value?: string): string | undefined => {
    if (!value) return undefined
    const normalized = value.toLowerCase()
    if (normalized === 'macintel' || normalized.includes('mac')) return 'macOS'
    if (normalized.startsWith('win')) return 'Windows'
    if (normalized.includes('android')) return 'Android'
    if (
      normalized.includes('iphone') ||
      normalized.includes('ipad') ||
      normalized.includes('ipod')
    ) {
      return 'iOS'
    }
    if (normalized.includes('linux')) return 'Linux'
    return value
  }

  const osDisplay =
    resolveOsDisplay(osCandidate) ??
    resolveOsDisplay(
      userAgentString ? inferOsFromUserAgent(userAgentString) : undefined
    ) ??
    'Unknown'

  const languageRaw =
    toDisplayString(
      resolveMetadataValue([
        'language',
        'lang',
        'locale',
        'navigator.language',
        'navigator.languages[0]',
        'languages[0]',
        'headers.accept-language',
        'browser.language',
      ])
    ) ?? undefined

  const normalizeLanguage = (value: string): string => {
    const firstPart = value.split(',')[0]?.trim() ?? value
    const normalized = firstPart.replace('_', '-')
    if (normalized.includes('-')) {
      const [lang, region] = normalized.split('-')
      const languagePart = lang?.toLowerCase() ?? ''
      const regionPart = region ? `-${region.toUpperCase()}` : ''
      return `${languagePart}${regionPart}`
    }
    return normalized
  }

  const languageDisplay = languageRaw
    ? normalizeLanguage(languageRaw)
    : 'Unknown'

  const country =
    toDisplayString(
      resolveMetadataValue([
        'location.country',
        'location.countryName',
        'geo.country',
        'geo.countryName',
        'country',
        'countryName',
        'context.country',
      ])
    ) ?? undefined

  const currencyRaw =
    toDisplayString(
      resolveMetadataValue([
        'currency',
        'locale.currency',
        'geo.currency',
        'location.currency',
      ])
    ) ?? undefined

  const currencyDisplay = currencyRaw ? currencyRaw.toUpperCase() : 'Unknown'

  const timezoneRaw =
    toDisplayString(
      resolveMetadataValue([
        'timezone',
        'timeZone',
        'tz',
        'location.timezone',
        'location.timeZone',
        'geo.timezone',
        'navigator.timezone',
      ])
    ) ?? undefined

  const timezoneDisplay = timezoneRaw ?? 'Unknown'

  const currentUrlRaw =
    toDisplayString(
      resolveMetadataValue([
        'currentUrl',
        'url',
        'pageUrl',
        'location.url',
        'location.href',
        'context.url',
      ])
    ) ?? undefined

  const currentUrlDisplay = isWebPlatform
    ? (currentUrlRaw ?? 'Unknown')
    : 'Unavailable for this channel'

  const currentUrlLink =
    isWebPlatform && currentUrlRaw && /^https?:\/\//i.test(currentUrlRaw.trim())
      ? currentUrlRaw.trim()
      : undefined

  const ipRaw =
    toDisplayString(
      resolveMetadataValue([
        'ip',
        'ipAddress',
        'clientIp',
        'client.ip',
        'headers.x-forwarded-for',
        'request.ip',
      ])
    ) ?? undefined

  const ipAddressValue = ipRaw
    ? (ipRaw
        .split(',')
        .map(part => part.trim())
        .find(Boolean) ?? ipRaw.trim())
    : undefined

  const ipDisplay = isWebPlatform
    ? (ipAddressValue ?? 'Unknown')
    : 'Unavailable for this channel'

  const city =
    toDisplayString(
      resolveMetadataValue([
        'location.city',
        'geo.city',
        'city',
        'context.city',
      ])
    ) ?? undefined

  const region =
    toDisplayString(
      resolveMetadataValue([
        'location.region',
        'location.state',
        'location.province',
        'geo.region',
        'geo.state',
        'state',
        'province',
      ])
    ) ?? undefined

  const latitude =
    toDisplayString(
      resolveMetadataValue([
        'location.latitude',
        'location.lat',
        'geo.lat',
        'geo.latitude',
        'lat',
        'latitude',
      ])
    ) ?? undefined

  const longitude =
    toDisplayString(
      resolveMetadataValue([
        'location.longitude',
        'location.lon',
        'geo.lon',
        'geo.longitude',
        'lng',
        'long',
        'longitude',
      ])
    ) ?? undefined

  const baseLocationSegments = [city, region].filter(Boolean)
  const locationDisplay =
    baseLocationSegments.length > 0
      ? Array.from(
          new Set([
            ...baseLocationSegments,
            ...(country && !baseLocationSegments.includes(country)
              ? [country]
              : []),
          ])
        ).join(', ')
      : latitude && longitude
        ? `${latitude}, ${longitude}`
        : (country ?? 'Unknown')

  const browserHelper =
    userAgentString && userAgentString !== browserDisplay
      ? userAgentString
      : undefined

  const infoItems = [
    { label: 'Chat Session ID', value: session.id },
    { label: 'User', value: userDisplay },
    { label: 'Assigned Agent', value: assignedAgentInfoValue },
    { label: 'Assigned Team', value: assignedTeamInfoValue },
    { label: 'Handover Status', value: assignmentStatusDisplay },
    {
      label: 'Handover Requested',
      value: assignmentRequestedAtDisplay ?? '—',
    },
    { label: 'Creation Time', value: createdAtDisplay },
    { label: 'Last Message', value: lastMessageDisplay },
    {
      label: 'Browser',
      value: browserDisplay,
      helper: browserHelper,
    },
    { label: 'Language', value: languageDisplay },
    { label: 'Country', value: country ?? 'Unknown' },
    { label: 'Currency', value: currencyDisplay },
    {
      label: 'Current URL',
      value: currentUrlDisplay,
      link: currentUrlLink,
      muted: !isWebPlatform,
    },
    {
      label: 'IP Address',
      value: ipDisplay,
      muted: !isWebPlatform,
    },
    { label: 'Location', value: locationDisplay },
    { label: 'Operating System', value: osDisplay },
    { label: 'Timezone', value: timezoneDisplay },
  ] satisfies Array<{
    label: string
    value: string
    link?: string
    helper?: string
    muted?: boolean
  }>

  return !sessionId ? (
    <div className="p-6 text-center text-sm text-gray-500">
      No chat session selected.
    </div>
  ) : (
    <div className={containerClass}>
      {/* Header */}
      <header className="bg-white shadow-sm border-b">
        <div className={headerWrapperClass}>
          <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
            <div className="flex items-center space-x-4">
              <button
                onClick={() => handleExitToDashboard()}
                className="p-2 hover:bg-gray-100 rounded-md"
              >
                <ArrowLeft className="h-5 w-5" />
              </button>
              <div className="flex items-center space-x-2">
                {getPlatformIcon(session.platform)}
                <div>
                  <div className="flex items-center gap-2">
                    <h1 className="text-lg font-semibold text-gray-900">
                      {userDisplay}
                    </h1>
                    {session.status === 'completed' && (
                      <Badge
                        variant="secondary"
                        className="text-xs bg-gray-100 text-gray-700"
                      >
                        Completed
                      </Badge>
                    )}
                  </div>
                  <p className="text-sm text-gray-500">
                    Session {session.id.slice(-8).toUpperCase()} •{' '}
                    {session.platform} • Started {createdAtDisplay}
                  </p>
                  <div className="mt-2 flex flex-wrap gap-2">
                    <Badge
                      variant={displayAgentName ? 'outline' : 'secondary'}
                      className="flex items-center gap-1 text-xs"
                    >
                      <User className="h-3 w-3" />
                      <span>{assignedAgentBadgeLabel}</span>
                    </Badge>
                    <Badge
                      variant={assignedTeamId != null ? 'outline' : 'secondary'}
                      className="flex items-center gap-1 text-xs"
                    >
                      <Users className="h-3 w-3" />
                      <span>{assignedTeamBadgeLabel}</span>
                    </Badge>
                  </div>
                </div>
              </div>
            </div>

            <div className="flex flex-col items-stretch gap-3 lg:flex-row lg:items-center lg:justify-end">
              {isAssignedToMe && !pendingTransfer && (
                <div className="flex items-center gap-3">
                  <button
                    onClick={() => {
                      if (!canTransfer) {
                        notificationActions.showError(
                          'Permission denied',
                          'You do not have access to transfer sessions.'
                        )
                        return
                      }
                      setShowTransferModal(true)
                    }}
                    className={cn(
                      'flex items-center px-4 py-2 rounded-md text-white',
                      canTransfer
                        ? 'bg-blue-600 hover:bg-blue-700'
                        : 'bg-gray-400 cursor-not-allowed'
                    )}
                    disabled={!canTransfer}
                  >
                    <UserPlus className="h-4 w-4 mr-2" />
                    Transfer
                  </button>
                  <button
                    onClick={() => {
                      if (sessionIsCompleted) return
                      completeSession()
                    }}
                    disabled={sessionIsCompleted}
                    className={cn(
                      'flex items-center px-4 py-2 rounded-md',
                      sessionIsCompleted
                        ? 'bg-gray-300 text-gray-600 cursor-not-allowed'
                        : 'bg-green-600 text-white hover:bg-green-700'
                    )}
                  >
                    <CheckCircle className="h-4 w-4 mr-2" />
                    {sessionIsCompleted
                      ? 'Session Completed'
                      : 'Complete Session'}
                  </button>
                </div>
              )}

              {isAssignedToMe && pendingTransfer && (
                <div className="flex items-center gap-3">
                  <button
                    onClick={completeSession}
                    disabled
                    className="flex items-center px-4 py-2 bg-gray-400 text-white rounded-md cursor-not-allowed"
                    title="Cannot complete session while transfer is pending"
                  >
                    <CheckCircle className="h-4 w-4 mr-2" />
                    Complete Session
                  </button>
                </div>
              )}

              {!isAssignedToMe && (
                <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:gap-4">
                  {isAssignedToAnotherAgent && (
                    <div
                      className={`flex items-center gap-2 rounded-md px-3 py-2 text-sm ${
                        isAssignedToAnotherAgent
                          ? 'border border-amber-200 bg-amber-50 text-amber-800'
                          : 'border border-blue-200 bg-blue-50 text-blue-700'
                      }`}
                    >
                      <AlertCircle className="h-4 w-4" />
                      <span>
                        {isAssignedToAnotherAgent
                          ? `Assigned to ${assignedAgentName || 'another agent'}.`
                          : 'Session is available to take over.'}
                      </span>
                    </div>
                  )}

                  <div className="flex flex-wrap items-center gap-2">
                    {/* Show green Take Over/Accept button only if NOT a supervisor viewing another agent's session (red button handles that) */}
                    {(isSessionAvailable ||
                      (!isSessionAvailable &&
                        pendingTransfer &&
                        !isAssignedToMe)) &&
                      !(isAssignedToAnotherAgent && isSupervisor) && (
                        <button
                          onClick={async () => {
                            if (canAcceptPendingTransfer) {
                              await handleAcceptTransfer()
                              return
                            }

                            if (sessionId) {
                              await handleTakeoverSession(sessionId)
                            }
                          }}
                          disabled={
                            canAcceptPendingTransfer
                              ? isAcceptingTransfer
                              : isTakeoverActionPending
                          }
                          className="flex items-center px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          <CheckCircle className="h-4 w-4 mr-2" />
                          {canAcceptPendingTransfer
                            ? isAcceptingTransfer
                              ? 'Accepting...'
                              : 'Accept'
                            : 'Take Over'}
                        </button>
                      )}

                    {/* Supervisor force takeover (red button) */}
                    {isAssignedToAnotherAgent && isSupervisor && (
                      <button
                        onClick={async () => {
                          if (sessionId) {
                            await handleTakeoverSession(sessionId, {
                              action: 'force_takeover',
                            })
                          }
                        }}
                        disabled={isTakeoverActionPending}
                        className="flex items-center px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        <CheckCircle className="h-4 w-4 mr-2" />
                        Takeover
                      </button>
                    )}

                    {isSupervisor && (
                      <button
                        onClick={openSupervisorManagement}
                        className="flex items-center px-4 py-2 bg-purple-600 text-white rounded-md hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed"
                        disabled={
                          isSupervisorActionLoading || isTakeoverActionPending
                        }
                      >
                        <UserPlus className="h-4 w-4 mr-2" />
                        Manage
                      </button>
                    )}
                  </div>
                </div>
              )}

              <button
                type="button"
                onClick={toggleSidebar}
                aria-pressed={!isSidebarCollapsed}
                aria-expanded={!isSidebarCollapsed}
                className="flex items-center justify-center gap-2 self-start rounded-md border border-gray-200 bg-white px-3 py-2 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-50 lg:self-auto"
              >
                {isSidebarCollapsed ? (
                  <>
                    <PanelRightOpen className="h-4 w-4" />
                    <span>Show details</span>
                  </>
                ) : (
                  <>
                    <PanelRightClose className="h-4 w-4" />
                    <span>Hide details</span>
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Messages */}
      <div className="flex-1 w-full">
        <div className={mainContentWrapperClass}>
          <div className="flex-1 bg-white rounded-lg shadow-sm border flex flex-col h-[80vh]">
            <div
              ref={messagesContainerRef}
              className="flex-1 overflow-y-auto p-4 space-y-4"
            >
              {messages
                .slice()
                .sort((a, b) => {
                  const timeA = new Date(a.timestamp).getTime()
                  const timeB = new Date(b.timestamp).getTime()
                  return timeA - timeB
                })
                .map(message => (
                  <div
                    key={message.id}
                    className={`flex ${message.role === 'agent' || message.role === 'assistant' ? 'justify-end' : 'justify-start'}`}
                  >
                    <div
                      className={`max-w-xs lg:max-w-md rounded-lg px-4 py-2 ${
                        message.role === 'user'
                          ? 'bg-gray-200 text-gray-800'
                          : message.role === 'agent' ||
                              message.role === 'assistant'
                            ? 'bg-blue-600 text-white'
                            : 'bg-yellow-100 text-yellow-800'
                      } flex flex-col gap-2`}
                    >
                      {(message.role === 'agent' ||
                        message.role === 'assistant') &&
                        message.agentName && (
                          <div className="flex items-center mb-1">
                            <User className="w-3 h-3 mr-1" />
                            <span className="text-xs font-medium opacity-75">
                              {message.agentName}
                            </span>
                          </div>
                        )}
                      {message.role === 'user' && userSenderLabel && (
                        <div className="flex items-center mb-1 gap-2">
                          <User className="w-3 h-3" />
                          <span className="text-xs font-medium opacity-75">
                            {userSenderLabel}
                          </span>
                          {session.status === 'completed' && (
                            <span className="inline-flex items-center rounded-full bg-gray-100 px-2 py-0.5 text-[10px] font-semibold text-gray-700">
                              Completed
                            </span>
                          )}
                        </div>
                      )}
                      <MessageAttachments
                        attachments={message.attachments}
                        legacyImageUrl={message.imageUrl}
                        align="none"
                      />
                      {message.audioUrl && (
                        <div className="mb-2">
                          <audio controls className="w-full max-w-xs">
                            <source src={message.audioUrl} type="audio/webm" />
                            Your browser does not support the audio element.
                          </audio>
                        </div>
                      )}
                      {message.fileUrl && message.fileName && (
                        <div className="mb-2 p-2 bg-gray-100 rounded flex items-center space-x-2">
                          <Paperclip className="h-4 w-4 text-gray-500" />
                          <a
                            href={message.fileUrl}
                            download={message.fileName}
                            className="text-sm text-blue-600 hover:text-blue-800 underline"
                          >
                            {message.fileName}
                          </a>
                        </div>
                      )}
                      {message.content && !message.audioUrl ? (
                        <ChatMarkdown>{message.content}</ChatMarkdown>
                      ) : null}
                      <p className="text-xs opacity-70 mt-1">
                        {message.timestamp
                          ? new Date(message.timestamp).toLocaleTimeString()
                          : ''}
                      </p>
                      {/* Translate EN button for user messages */}
                      {message.role === 'user' && message.needsTranslation && (
                        <div className="mt-2">
                          {message.showTranslation ? (
                            <div className="pt-2 border-t border-gray-200/30">
                              <button
                                type="button"
                                onClick={() => {
                                  setMessages(prev =>
                                    prev.map(msg =>
                                      msg.id === message.id
                                        ? { ...msg, showTranslation: false }
                                        : msg
                                    )
                                  )
                                }}
                                className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wide opacity-50 hover:opacity-70 mb-1"
                              >
                                <Languages className="h-3 w-3" />
                                Hide Translation
                              </button>
                              <p className="text-xs opacity-70 italic">
                                {message.translation}
                              </p>
                            </div>
                          ) : (
                            <button
                              type="button"
                              onClick={async () => {
                                if (message.translation) {
                                  setMessages(prev =>
                                    prev.map(msg =>
                                      msg.id === message.id
                                        ? { ...msg, showTranslation: true }
                                        : msg
                                    )
                                  )
                                  return
                                }

                                const messageIdForApi =
                                  message.dbMessageId || message.id

                                // Set loading state
                                setMessages(prev =>
                                  prev.map(msg =>
                                    msg.id === message.id
                                      ? { ...msg, isTranslating: true }
                                      : msg
                                  )
                                )

                                try {
                                  const result =
                                    await api.sessions.getMessageTranslation(
                                      sessionId as string,
                                      messageIdForApi,
                                      'en'
                                    )

                                  if (result.success && result.data) {
                                    const translationContent =
                                      result.data &&
                                      typeof result.data === 'object' &&
                                      'content' in result.data
                                        ? (result.data as any).content
                                        : null

                                    if (translationContent) {
                                      setMessages(prev =>
                                        prev.map(msg =>
                                          msg.id === message.id
                                            ? {
                                                ...msg,
                                                translation:
                                                  translationContent as string,
                                                showTranslation: true,
                                                isTranslating: false,
                                                hasTranslation: true,
                                                needsTranslation:
                                                  typeof msg.content ===
                                                    'string' &&
                                                  msg.content.trim().length > 0
                                                    ? String(
                                                        translationContent
                                                      ) !== msg.content
                                                    : true,
                                              }
                                            : msg
                                        )
                                      )
                                    } else {
                                      // Clear loading state if no content
                                      setMessages(prev =>
                                        prev.map(msg =>
                                          msg.id === message.id
                                            ? { ...msg, isTranslating: false }
                                            : msg
                                        )
                                      )
                                    }
                                  } else {
                                    // Clear loading state on failure
                                    setMessages(prev =>
                                      prev.map(msg =>
                                        msg.id === message.id
                                          ? { ...msg, isTranslating: false }
                                          : msg
                                      )
                                    )
                                  }
                                } catch (error) {
                                  console.error(
                                    'Error fetching translation:',
                                    error
                                  )
                                  // Clear loading state on error
                                  setMessages(prev =>
                                    prev.map(msg =>
                                      msg.id === message.id
                                        ? { ...msg, isTranslating: false }
                                        : msg
                                    )
                                  )
                                }
                              }}
                              disabled={message.isTranslating}
                              className="inline-flex items-center gap-1 text-[10px] font-medium text-white/70 hover:text-white disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                              {message.isTranslating ? (
                                <>
                                  <svg
                                    className="h-3 w-3 animate-spin"
                                    xmlns="http://www.w3.org/2000/svg"
                                    fill="none"
                                    viewBox="0 0 24 24"
                                  >
                                    <circle
                                      className="opacity-25"
                                      cx="12"
                                      cy="12"
                                      r="10"
                                      stroke="currentColor"
                                      strokeWidth="4"
                                    />
                                    <path
                                      className="opacity-75"
                                      fill="currentColor"
                                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                                    />
                                  </svg>
                                  Translating...
                                </>
                              ) : (
                                <>
                                  <Languages className="h-3 w-3" />
                                  Translate EN
                                </>
                              )}
                            </button>
                          )}
                        </div>
                      )}
                      {(message.role === 'agent' ||
                        message.role === 'assistant') &&
                        message.content?.trim() &&
                        canManageArticles && (
                          <div className="mt-2 flex justify-end">
                            <button
                              type="button"
                              onClick={() => openSaveArticleDialog(message)}
                              className="inline-flex items-center gap-1 text-[11px] font-medium text-white/80 hover:text-white"
                            >
                              <NotebookPen className="h-3 w-3" />
                              Save as article
                            </button>
                          </div>
                        )}
                    </div>
                  </div>
                ))}

              {/* User typing indicator */}
              {userTyping && (
                <div className="flex justify-start mb-2">
                  <TypingIndicator text="User is typing..." />
                </div>
              )}

              <div ref={messagesEndRef} />
            </div>

            {/* Input */}
            <div className="border-t p-4">
              {acceptanceBannerConfig && (
                <div className="mb-3 rounded-lg border border-yellow-200 bg-yellow-50 p-3">
                  <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                    <div className="flex items-start gap-2">
                      <AlertCircle className="h-5 w-5 text-yellow-600 mt-0.5" />
                      <div>
                        <p className="text-sm font-semibold text-yellow-800">
                          {acceptanceBannerConfig.title}
                        </p>
                        <p className="mt-1 text-xs text-yellow-700">
                          {acceptanceBannerConfig.description}
                        </p>
                        {acceptanceBannerConfig.extra && (
                          <p className="mt-1 text-xs text-yellow-700">
                            {acceptanceBannerConfig.extra}
                          </p>
                        )}
                        {acceptanceBannerConfig.meta && (
                          <p className="mt-1 text-[11px] text-yellow-600">
                            {acceptanceBannerConfig.meta}
                          </p>
                        )}
                      </div>
                    </div>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={acceptanceBannerConfig.onClick}
                      disabled={acceptanceBannerConfig.buttonDisabled}
                      className="self-start"
                    >
                      {acceptanceBannerConfig.buttonLabel}
                    </Button>
                  </div>
                </div>
              )}
              {/* File Preview */}
              {uploadedFile && (
                <div className="mb-3 p-3 bg-gray-50 rounded-lg border">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-2">
                      <Paperclip className="h-4 w-4 text-gray-500" />
                      <span className="text-sm text-gray-700">
                        {uploadedFile.type === 'voice'
                          ? 'Voice message recorded'
                          : uploadedFile.type === 'image'
                            ? uploadedFile.isUploading
                              ? 'Uploading image...'
                              : uploadedFile.uploadError
                                ? 'Image upload failed (using local preview)'
                                : 'Image ready'
                            : uploadedFile.type === 'file'
                              ? uploadedFile.isUploading
                                ? 'Uploading file...'
                                : uploadedFile.uploadError
                                  ? 'File upload failed (using local preview)'
                                  : 'File ready'
                              : uploadedFile.file.name}
                      </span>
                      {uploadedFile.type === 'voice' && (
                        <audio controls className="h-8">
                          <source src={uploadedFile.url} type="audio/webm" />
                        </audio>
                      )}
                      {uploadedFile.isUploading && (
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600"></div>
                      )}
                    </div>
                    <button
                      onClick={removeUploadedFile}
                      className="p-1 hover:bg-gray-200 rounded"
                    >
                      <X className="h-4 w-4 text-gray-500" />
                    </button>
                  </div>
                  {uploadedFile.type === 'image' && (
                    <div className="mt-2">
                      <NextImage
                        src={uploadedFile.url}
                        alt="Preview"
                        width={128}
                        height={128}
                        className="max-w-32 max-h-32 object-cover rounded"
                      />
                    </div>
                  )}
                </div>
              )}

              <div className="flex items-center space-x-2">
                <div className="flex-1 relative">
                  <Textarea
                    ref={inputRef}
                    value={inputText}
                    onChange={event => {
                      const newValue = event.target.value
                      setInputText(newValue)
                      handleAgentTyping(newValue)
                    }}
                    onKeyDown={event => {
                      if (!canReplyToSession) {
                        event.preventDefault()
                        return
                      }
                      if (event.key === 'Enter' && !event.shiftKey) {
                        event.preventDefault()
                        shouldRestoreFocusRef.current = true
                        sendMessage()
                      }
                    }}
                    placeholder={
                      !isAssignedToMe
                        ? 'This session is not assigned to you.'
                        : isAssignmentPendingForCurrentAgent
                          ? 'Accept the handover request to reply.'
                          : 'Type your message...'
                    }
                    className="border border-gray-300"
                    disabled={isSending || !canReplyToSession}
                    rows={1}
                  />
                </div>

                <button
                  type="button"
                  onClick={openKnowledgeModal}
                  className={cn(
                    'p-2 rounded-lg',
                    canUseKnowledgeBase
                      ? 'bg-gray-200 text-gray-600 hover:bg-gray-300'
                      : 'bg-gray-100 text-gray-400 cursor-not-allowed'
                  )}
                  aria-label="Search knowledge base"
                  disabled={!canUseKnowledgeBase}
                >
                  <Sparkles className="w-5 h-5" />
                </button>

                <button
                  type="button"
                  onClick={openArticleLibrary}
                  className={cn(
                    'p-2 rounded-lg',
                    canReplyToSession && canManageArticles
                      ? 'bg-gray-200 text-gray-600 hover:bg-gray-300'
                      : 'bg-gray-100 text-gray-400 cursor-not-allowed'
                  )}
                  disabled={!canReplyToSession || !canManageArticles}
                  aria-label="Insert saved article"
                >
                  <BookOpen className="w-5 h-5" />
                </button>

                {/* File Upload Button */}
                <label
                  className={`p-2 rounded-lg cursor-pointer ${
                    canReplyToSession
                      ? 'bg-gray-200 text-gray-600 hover:bg-gray-300'
                      : 'bg-gray-100 text-gray-400 cursor-not-allowed'
                  }`}
                >
                  <Paperclip className="w-5 h-5" />
                  <input
                    type="file"
                    onChange={handleFileUpload}
                    className="hidden"
                    accept="image/*,application/pdf,.doc,.docx,.txt"
                    disabled={isSending || !canReplyToSession}
                  />
                </label>

                {/* Voice Recording Button */}
                <button
                  onClick={isRecording ? stopRecording : startRecording}
                  className={`p-2 rounded-lg ${
                    isRecording
                      ? 'bg-red-600 text-white animate-pulse'
                      : canReplyToSession
                        ? 'bg-gray-200 text-gray-600 hover:bg-gray-300'
                        : 'bg-gray-100 text-gray-400 cursor-not-allowed'
                  }`}
                  disabled={isSending || !canReplyToSession}
                >
                  {isRecording ? (
                    <MicOff className="w-5 h-5" />
                  ) : (
                    <Mic className="w-5 h-5" />
                  )}
                </button>

                {/* Send Button */}
                <button
                  onClick={() =>
                    sendMessage(
                      uploadedFile?.type === 'voice'
                        ? 'voice'
                        : uploadedFile?.type === 'file'
                          ? 'file'
                          : 'text'
                    )
                  }
                  disabled={
                    (!inputText.trim() && !uploadedFile) ||
                    isSending ||
                    uploadedFile?.isUploading ||
                    !canReplyToSession
                  }
                  className="p-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <Send className="w-5 h-5" />
                </button>
              </div>
            </div>
          </div>
          <aside
            className={`w-full lg:w-60 xl:w-72 ${isSidebarCollapsed ? 'hidden' : ''}`}
          >
            <div className="bg-white rounded-lg shadow-sm border p-4 space-y-4">
              <ContactSection
                contactMatch={contactMatch}
                contactDisplayName={contactDisplayName}
                contactIdDisplay={contactIdDisplay}
                isSupervisor={isSupervisor}
                form={contactForm}
              />
              {shouldShowPendingTransferCard && pendingTransfer && (
                <div className="rounded-md border border-yellow-200 bg-yellow-50 p-3">
                  <div className="flex items-start gap-2">
                    <AlertCircle className="w-5 h-5 text-yellow-500 mt-0.5 shrink-0" />
                    <div className="flex-1">
                      <div className="flex items-center justify-between gap-2">
                        <p className="text-sm font-semibold text-yellow-800">
                          Transfer Pending
                        </p>
                        <Badge
                          variant="outline"
                          className="text-xs uppercase tracking-wide"
                        >
                          {pendingTransferStatusLabel}
                        </Badge>
                      </div>
                      <p className="mt-1 text-xs text-yellow-700">
                        {pendingTransferDescription}
                      </p>
                      {pendingTransferTargetLabel && (
                        <p className="mt-1 text-xs text-yellow-700">
                          Target: {pendingTransferTargetLabel}
                          {pendingTransferTeamLabel
                            ? ' (Team)'
                            : pendingTransferAgentLabel
                              ? ' (Agent)'
                              : ''}
                        </p>
                      )}
                      <p className="mt-1 text-xs text-yellow-700">
                        Reason: {pendingTransferReason}
                      </p>
                      {pendingTransferRequestedAt && (
                        <p className="mt-1 text-xs text-yellow-600">
                          Requested {pendingTransferRequestedAt}
                        </p>
                      )}
                      {canAcceptPendingTransfer ? (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={handleAcceptTransfer}
                          disabled={isAcceptingTransfer}
                          className="mt-3"
                        >
                          {isAcceptingTransfer
                            ? 'Accepting...'
                            : 'Accept Request'}
                        </Button>
                      ) : null}
                      {canCancelPendingTransfer ? (
                        <button
                          onClick={handleCancelTransfer}
                          className="mt-3 text-xs font-medium text-yellow-800 hover:text-yellow-900 underline whitespace-nowrap"
                        >
                          Cancel Transfer
                        </button>
                      ) : null}
                    </div>
                  </div>
                </div>
              )}
              <ul className="space-y-4">
                {infoItems.map(item => (
                  <li key={item.label} className="flex flex-col">
                    <span className="text-xs font-medium uppercase tracking-wide text-gray-500">
                      {item.label}
                    </span>
                    {item.link ? (
                      <a
                        href={item.link}
                        target="_blank"
                        rel="noreferrer"
                        className={`text-sm font-semibold break-all ${
                          item.muted
                            ? 'text-gray-500 hover:text-gray-600'
                            : 'text-blue-600 hover:text-blue-700'
                        }`}
                      >
                        {item.value}
                      </a>
                    ) : (
                      <span
                        className={`text-sm wrap-break-word ${
                          item.muted
                            ? 'text-gray-500 font-medium'
                            : 'text-gray-900 font-semibold'
                        }`}
                      >
                        {item.value}
                      </span>
                    )}
                    {item.helper && (
                      <span className="text-xs text-gray-500 break-all">
                        {item.helper}
                      </span>
                    )}
                  </li>
                ))}
              </ul>
            </div>
          </aside>
        </div>
      </div>

      {isSupervisor && sessionId && (
        <SupervisorManageDialog
          open={isSupervisorModalOpen}
          onOpenChange={open => {
            setIsSupervisorModalOpen(open)
            if (!open) {
              setSupervisorActionError(null)
            }
          }}
          sessionSummary={{
            id: session?.id ?? sessionId ?? null,
            platform: session?.platform ?? null,
            assignedAgentName,
            assignedAgentId: assignedAgentId,
            assignedTeamId,
            assignedTeamName,
            pendingHandoverRequest: pendingTransfer
              ? {
                  teamId: pendingTransfer.teamId ?? null,
                  teamName: pendingTransfer.teamName ?? null,
                  assignedTo: pendingTransfer.assignedToAgentId ?? null,
                  assignedToName:
                    pendingTransfer.assignedToAgentName ??
                    pendingTransfer.assignedToAgentUsername ??
                    null,
                }
              : null,
            lastAssignment: session?.assignment ?? null,
          }}
          myTeamIds={agentTeamIds}
          agents={availableAgentsForSupervisor}
          isLoadingAgents={isFetchingSupervisorAgents}
          agentsError={availableAgentsError}
          onSubmit={handleSupervisorSubmit}
          isSubmitting={isSupervisorActionLoading}
          isTakeoverPending={isTakeoverActionPending}
          actionError={supervisorActionError}
          defaultTeamId={supervisorDefaultTeamId}
          actionLabel="Reassign"
        />
      )}

      {sessionId && (
        <Dialog
          open={isKnowledgeModalOpen}
          onOpenChange={open => {
            if (open) {
              setIsKnowledgeModalOpen(true)
            } else {
              closeKnowledgeModal()
            }
          }}
        >
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle>Knowledge Search</DialogTitle>
              <DialogDescription>
                Look up curated knowledge base answers and insert them into your
                reply.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4">
              <form
                onSubmit={handleKnowledgeSearch}
                className="flex flex-col gap-2 sm:flex-row"
              >
                <Input
                  value={knowledgeQuery}
                  onChange={event => setKnowledgeQuery(event.target.value)}
                  placeholder="Ask a question or search keywords"
                  autoFocus
                  disabled={isKnowledgeSearching}
                />
                <Button
                  type="submit"
                  disabled={
                    isKnowledgeSearching || knowledgeQuery.trim().length < 2
                  }
                >
                  {isKnowledgeSearching ? 'Searching…' : 'Search'}
                </Button>
              </form>

              {knowledgeErrorMessage && (
                <p className="text-sm text-red-600">{knowledgeErrorMessage}</p>
              )}

              {knowledgeAnswerData && (
                <div className="space-y-4">
                  <div className="rounded-lg border border-blue-200 bg-blue-50 p-4">
                    <h4 className="text-sm font-semibold text-blue-700">
                      Suggested answer
                    </h4>
                    <div className="mt-2 prose prose-sm max-w-none text-gray-800">
                      {knowledgeAnswer ? (
                        <ChatMarkdown>{knowledgeAnswer}</ChatMarkdown>
                      ) : (
                        <p className="text-sm text-gray-600">
                          No direct answer was generated. Review the sources
                          below for helpful context.
                        </p>
                      )}
                    </div>
                  </div>

                  {knowledgeSources.length > 0 && (
                    <div className="space-y-2">
                      <h4 className="text-sm font-semibold text-gray-700">
                        Sources
                      </h4>
                      <div className="max-h-60 overflow-y-auto space-y-2 pr-1">
                        {knowledgeSources.map((source, index) => (
                          <div
                            key={`${source.document_id ?? index}-${index}`}
                            className="rounded-md border border-gray-200 bg-white p-3 shadow-sm"
                          >
                            <div className="flex items-center justify-between">
                              <span className="text-xs font-semibold text-gray-600">
                                Source {index + 1}
                              </span>
                              <Badge variant="outline">
                                Score {source.score.toFixed(2)}
                              </Badge>
                            </div>

                            {Boolean(source.metadata?.title) && (
                              <p className="mt-1 text-xs font-medium text-gray-700">
                                {String(source.metadata?.title as string)}
                              </p>
                            )}
                            <p className="mt-2 text-xs text-gray-600 whitespace-pre-wrap">
                              {source.text}
                            </p>
                            {Boolean(source.metadata?.url) && (
                              <a
                                href={String(source.metadata?.url)}
                                target="_blank"
                                rel="noreferrer"
                                className="mt-2 inline-flex text-xs text-blue-600 hover:text-blue-800"
                              >
                                View source
                              </a>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}

              {!knowledgeAnswerData &&
                !knowledgeErrorMessage &&
                !isKnowledgeSearching && (
                  <p className="text-sm text-muted-foreground">
                    Results will appear here after you run a search.
                  </p>
                )}
            </div>
            <DialogFooter className="flex flex-col gap-2 sm:flex-row sm:justify-end">
              <Button
                type="button"
                variant="ghost"
                onClick={closeKnowledgeModal}
              >
                Close
              </Button>
              <Button
                type="button"
                onClick={handleInsertKnowledgeAnswer}
                disabled={!knowledgeAnswer || isKnowledgeSearching}
              >
                Insert Answer
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}

      {sessionId && (
        <Dialog
          open={isArticleLibraryOpen}
          onOpenChange={open => {
            setIsArticleLibraryOpen(open)
            if (!open) {
              setArticleSearchTerm('')
              setArticlePreviewId(null)
            }
          }}
        >
          <DialogContent className="max-w-3xl">
            <DialogHeader>
              <DialogTitle>Insert saved article</DialogTitle>
              <DialogDescription>
                Search the article library and insert a polished reply into the
                composer.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4">
              <Input
                value={articleSearchTerm}
                onChange={event => setArticleSearchTerm(event.target.value)}
                placeholder="Search by keywords or sample questions"
                autoFocus
              />
              <div className="max-h-[60vh] overflow-y-auto space-y-3">
                {normalizedArticleSearch.length < 2 ? (
                  <p className="text-sm text-muted-foreground">
                    Type at least two characters to see matching saved replies.
                  </p>
                ) : articleSuggestionsQuery.isLoading ? (
                  <p className="text-sm text-muted-foreground">
                    Searching the library…
                  </p>
                ) : articleSuggestionsQuery.isError ? (
                  <p className="text-sm text-red-600">
                    Unable to load the article library. Please try again.
                  </p>
                ) : articleSuggestions.length > 0 ? (
                  articleSuggestions.map(article => (
                    <div
                      key={article.id}
                      className="rounded-lg border border-gray-200 p-4 space-y-3"
                    >
                      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                        <div className="space-y-1">
                          <p className="text-sm font-semibold text-gray-900">
                            {article.title}
                          </p>
                          {article.summary && (
                            <p className="text-xs text-muted-foreground line-clamp-2">
                              {article.summary}
                            </p>
                          )}
                          {article.matchedQuestions?.length ? (
                            <div className="flex flex-wrap gap-1 pt-1">
                              {article.matchedQuestions
                                .slice(0, 4)
                                .map(question => (
                                  <Badge key={question} variant="outline">
                                    {question}
                                  </Badge>
                                ))}
                            </div>
                          ) : null}
                        </div>
                        <div className="flex items-center gap-2">
                          {typeof article.matchScore === 'number' && (
                            <Badge variant="secondary">
                              Score {article.matchScore}
                            </Badge>
                          )}
                          <Button
                            size="sm"
                            onClick={() => handleInsertArticleContent(article)}
                          >
                            Insert
                          </Button>
                        </div>
                      </div>
                      <button
                        type="button"
                        className="text-xs text-blue-600 hover:text-blue-700"
                        onClick={() =>
                          setArticlePreviewId(prev =>
                            prev === article.id ? null : article.id
                          )
                        }
                      >
                        {articlePreviewId === article.id
                          ? 'Hide preview'
                          : 'View preview'}
                      </button>
                      {articlePreviewId === article.id && (
                        <div className="rounded-md bg-gray-50 p-3 text-sm text-gray-700 whitespace-pre-wrap">
                          {article.content}
                        </div>
                      )}
                    </div>
                  ))
                ) : (
                  <p className="text-sm text-muted-foreground">
                    No saved replies matched those keywords yet.
                  </p>
                )}
              </div>
            </div>
          </DialogContent>
        </Dialog>
      )}

      {sessionId && canManageArticles && (
        <Dialog
          open={isSaveArticleOpen}
          onOpenChange={open => {
            if (!open) {
              closeSaveArticleDialog()
            }
          }}
        >
          <DialogContent className="max-w-xl">
            <DialogHeader>
              <DialogTitle>Save reply as article</DialogTitle>
              <DialogDescription>
                Capture refined responses so teammates can reuse them in future
                conversations.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="draft-article-title">Title</Label>
                <Input
                  id="draft-article-title"
                  value={articleDraft.title}
                  onChange={event =>
                    setArticleDraft(prev => ({
                      ...prev,
                      title: event.target.value,
                    }))
                  }
                  placeholder="Give this reply a clear name"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="draft-article-summary">Summary</Label>
                <Input
                  id="draft-article-summary"
                  value={articleDraft.summary}
                  onChange={event =>
                    setArticleDraft(prev => ({
                      ...prev,
                      summary: event.target.value,
                    }))
                  }
                  placeholder="Optional one-line description"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="draft-article-content">Content</Label>
                <Textarea
                  id="draft-article-content"
                  rows={6}
                  value={articleDraft.content}
                  onChange={event =>
                    setArticleDraft(prev => ({
                      ...prev,
                      content: event.target.value,
                    }))
                  }
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="draft-article-tags">Tags</Label>
                <Input
                  id="draft-article-tags"
                  value={articleDraft.tagsInput}
                  onChange={event =>
                    setArticleDraft(prev => ({
                      ...prev,
                      tagsInput: event.target.value,
                    }))
                  }
                  placeholder="Comma separated keywords (billing, escalation)"
                />
              </div>
              <div className="space-y-2">
                <Label>Sample questions</Label>
                <p className="text-xs text-muted-foreground">
                  Add the phrases customers usually ask so this reply appears in
                  keyword searches.
                </p>
                <div className="space-y-2">
                  {articleDraft.questions.map((question, index) => (
                    <div
                      key={`draft-question-${index}`}
                      className="flex items-center gap-2"
                    >
                      <Input
                        value={question}
                        onChange={event =>
                          updateDraftQuestion(index, event.target.value)
                        }
                      />
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => removeDraftQuestion(index)}
                      >
                        Remove
                      </Button>
                    </div>
                  ))}
                </div>
                <div className="flex items-center gap-2">
                  <Input
                    value={articleDraftQuestionInput}
                    onChange={event =>
                      setArticleDraftQuestionInput(event.target.value)
                    }
                    onKeyDown={event => {
                      if (event.key === 'Enter') {
                        event.preventDefault()
                        addDraftQuestion()
                      }
                    }}
                    placeholder="Add another example question"
                  />
                  <Button variant="outline" onClick={addDraftQuestion}>
                    Add
                  </Button>
                </div>
              </div>
            </div>
            <DialogFooter>
              <Button
                variant="ghost"
                onClick={closeSaveArticleDialog}
                disabled={saveArticleMutation.isPending}
              >
                Cancel
              </Button>
              <Button
                onClick={handleSaveArticleDraft}
                disabled={saveArticleMutation.isPending}
              >
                {saveArticleMutation.isPending ? 'Saving…' : 'Save article'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}

      {/* Team Selection Modal for Takeover */}
      <Dialog
        open={showTakeoverTeamModal}
        onOpenChange={setShowTakeoverTeamModal}
      >
        <DialogContent className="sm:max-w-[425px]">
          <DialogHeader>
            <DialogTitle>Select Team for Takeover</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <p className="text-sm text-gray-600">
              This session has no team assigned. Please select which team you
              want to takeover this session under:
            </p>
            <div className="space-y-2">
              <Label htmlFor="takeover-team-select">Team</Label>
              <Select
                value={selectedTakeoverTeamId?.toString() || ''}
                onValueChange={value =>
                  setSelectedTakeoverTeamId(Number(value))
                }
              >
                <SelectTrigger id="takeover-team-select">
                  <SelectValue placeholder="Select a team" />
                </SelectTrigger>
                <SelectContent>
                  {agentTeams.map(team => (
                    <SelectItem key={team.id} value={team.id.toString()}>
                      {team.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            {pendingTakeoverSessionId && (
              <div className="mt-4 p-3 bg-gray-50 rounded-md space-y-2">
                <div className="text-xs text-gray-500">Session ID</div>
                <div className="text-sm font-medium">
                  {pendingTakeoverSessionId.slice(-8).toUpperCase()}
                </div>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setShowTakeoverTeamModal(false)
                setPendingTakeoverSessionId(null)
                setPendingTakeoverOptions(undefined)
                setSelectedTakeoverTeamId(null)
              }}
              disabled={isTakeoverActionPending}
            >
              Cancel
            </Button>
            <Button
              onClick={handleConfirmTakeoverTeamSelection}
              disabled={
                isTakeoverActionPending || selectedTakeoverTeamId === null
              }
            >
              {isTakeoverActionPending ? 'Taking Over...' : 'Takeover Session'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Transfer Modal */}
      {sessionId ? (
        <TransferModal
          sessionId={sessionId}
          open={showTransferModal}
          onOpenChange={setShowTransferModal}
          onTransferComplete={handleTransferComplete}
          canTransfer={canTransfer}
        />
      ) : null}
    </div>
  )
}

export default AgentChatPanel
